import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";

// ═══════════════════════════════════════════════════════════════
// BRAND
// ═══════════════════════════════════════════════════════════════
const B = {
  red:"#D93025", blue:"#1A73E8", green:"#188038", yellow:"#F9AB00",
  dark:"#3C4043", bg:"#F8F9FA", card:"#FFFFFF",
  border:"#E8EAED", border2:"#DADCE0",
  text:"#3C4043", textSub:"#5F6368", textMut:"#9AA0A6",
};

// ═══════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════
const DEFAULT_CATEGORIES = [
  {name:"Receita de Vendas",color:B.green,type:"credit",subcategories:[]},
  {name:"Outras Receitas",color:"#34A853",type:"credit",subcategories:[]},
  {name:"Devoluções",color:B.red,type:"debit",subcategories:[]},
  {name:"Custo de Mercadoria",color:"#E67C00",type:"debit",subcategories:[]},
  {name:"Frete",color:B.yellow,type:"debit",subcategories:[]},
  {name:"Despesas com Pessoal",color:B.blue,type:"debit",subcategories:[]},
  {name:"Marketing",color:"#9C27B0",type:"debit",subcategories:[]},
  {name:"Despesas Fixas",color:"#1565C0",type:"debit",subcategories:[]},
  {name:"Despesas Operacionais",color:"#5F6368",type:"debit",subcategories:[]},
  {name:"Tarifas e Taxas",color:"#9AA0A6",type:"debit",subcategories:[]},
  {name:"Transferência Entrada",color:"#34A853",type:"credit",subcategories:[]},
  {name:"Transferência Saída",color:"#78909C",type:"debit",subcategories:[]},
  {name:"Aguardando Classificação",color:"#FF6F00",type:"both",subcategories:[]},
  {name:"Outros",color:"#BDBDBD",type:"both",subcategories:[]},
];

// Get category list for a company (falls back to defaults)
function getCompanyCategories(company) {
  if (company?.categories && company.categories.length > 0) return company.categories;
  return DEFAULT_CATEGORIES;
}

function getCategoryNames(company) {
  return getCompanyCategories(company).map(c=>c.name);
}

function getCatColor(catName, company) {
  const cats = getCompanyCategories(company);
  return cats.find(c=>c.name===catName)?.color || "#BDBDBD";
}

// Static fallback for places that don't have company context
const CATEGORIES = DEFAULT_CATEGORIES.map(c=>c.name);

const CAT_COLOR = Object.fromEntries(DEFAULT_CATEGORIES.map(c=>[c.name,c.color]));

// Which categories are OPEX (fixed for runway projection)
const OPEX_CATS = ["Despesas com Pessoal","Marketing","Despesas Fixas","Despesas Operacionais","Tarifas e Taxas","Frete"];

// MP TRANSACTION_TYPE → category
function mapMPTransactionType(txType) {
  const t = (txType||"").toLowerCase();
  if (t.includes("dinheiro recebido") || t.includes("liberação de dinheiro") || t.includes("transferência pix recebida") || t.includes("payment") || t.includes("sale") || t.includes("merchant_payment") || t.includes("pix_received") || t.includes("money_transfer_received")) return "Receita de Vendas";
  if (t.includes("dinheiro retido") || t.includes("reclamações") || t.includes("devoluções") || t.includes("refund") || t.includes("chargeback") || t.includes("dispute")) return "Devoluções";
  if (t.includes("fee") || t.includes("charge") || t.includes("tax") || t.includes("tarifa")) return "Tarifas e Taxas";
  if (t.includes("withdrawal") || t.includes("cashout") || t.includes("saque")) return "Transferência Saída";
  // Transfers OUT need classification
  if ((t.includes("transferência") || t.includes("transfer")) && !t.includes("recebida") && !t.includes("received")) return "Aguardando Classificação";
  return "";
}

const OMIE_MAP = [
  ["receita","Receita de Vendas"],["b2b","Receita de Vendas"],["venda","Receita de Vendas"],
  ["devolução","Devoluções"],["estorno","Devoluções"],
  ["entrada de transf","Transferência Entrada"],
  ["compras de mercad","Custo de Mercadoria"],["mercadoria","Custo de Mercadoria"],
  ["frete","Frete"],
  ["serviço de terceiro","Despesas com Pessoal"],["pró-labore","Despesas com Pessoal"],
  ["inss","Despesas com Pessoal"],["irpj","Despesas com Pessoal"],["csll","Despesas com Pessoal"],
  ["pis","Despesas Operacionais"],["cofins","Despesas Operacionais"],
  ["marketing","Marketing"],["tráfego","Marketing"],["publicidade","Marketing"],["ferramentas de mark","Marketing"],
  ["aluguel","Despesas Fixas"],["energia","Despesas Fixas"],["internet","Despesas Fixas"],
  ["água","Despesas Fixas"],["conservação","Despesas Fixas"],["coworking","Despesas Fixas"],
  ["segurança","Despesas Fixas"],["vigilância","Despesas Fixas"],
  ["tarifa","Tarifas e Taxas"],["taxa de reg","Tarifas e Taxas"],
  ["contabilidade","Despesas Operacionais"],["bpo","Despesas Operacionais"],
  ["sistema","Despesas Operacionais"],["software","Despesas Operacionais"],
  ["icms","Despesas Operacionais"],["aduaneiro","Despesas Operacionais"],
  ["embalagem","Despesas Operacionais"],["imposto","Despesas Operacionais"],["difal","Despesas Operacionais"],
];

function mapOmieCat(omieCategory, desc) {
  const c = ((omieCategory||"") + " " + (desc||"")).toLowerCase();
  for (const [k,v] of OMIE_MAP) if (c.includes(k)) return v;
  if (c.includes("omie") && c.includes("fitbank")) return "Tarifas e Taxas";
  return "Outros";
}

function parseB2BXLSX(buf) {
  const wb = XLSX.read(buf, {type:"array", cellDates:true});
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, {header:1, defval:""});
  if (!rows.length) return [];

  // Formato Omie "Contas a Receber Cliente":
  // Linha 1: título do relatório → pular
  // Linha 2: cabeçalhos das colunas → pular
  // Linha 3: total geral (sem cliente/data) → pular automaticamente
  // Linha 4+: dados (col A=cliente, B=data, C=tipo, D=situação, E=valor)

  // Situações que NÃO devem ser importadas
  const SKIP_SITS = new Set(["cancelado","cancelada","devolvido","devolvida","baixado","baixada"]);

  const projs = [];
  for (let i = 3; i < rows.length; i++) {  // começa na linha 4 (índice 3)
    const row = rows[i];
    const clientRaw = row[0]; // col A: cliente
    const dateRaw   = row[1]; // col B: data previsão
    const tipoRaw   = row[2]; // col C: tipo (1. Contas a Receber)
    const sitRaw    = row[3]; // col D: situação
    const valueRaw  = row[4]; // col E: valor

    if (!clientRaw || !dateRaw || !valueRaw) continue;

    const client = String(clientRaw).trim();
    if (!client || client.toLowerCase().includes("cliente ou fornecedor")) continue;

    // Filtrar situações inválidas
    const sit = String(sitRaw||"").toLowerCase();
    if (SKIP_SITS.has(sit.split(" ")[0])) continue;

    // Só importar Contas a Receber (não a pagar)
    const tipo = String(tipoRaw||"").toLowerCase();
    if (tipo && !tipo.includes("receber")) continue;

    // Parse date — suporta Date object, ISO string, DD/MM/YYYY
    let dateStr = "";
    if (dateRaw instanceof Date) {
      dateStr = dateRaw.toISOString().slice(0,10);
    } else {
      const s = String(dateRaw);
      const m1 = s.match(/(\d{4}-\d{2}-\d{2})/);
      if (m1) dateStr = m1[1];
      else {
        const m2 = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
        if (m2) dateStr = m2[3]+"-"+m2[2]+"-"+m2[1];
      }
    }
    if (!dateStr) continue;

    const value = parseFloat(String(valueRaw).replace(/[^\d\-\.]/g,"")) || 0;
    if (value <= 0) continue;

    projs.push({
      id: genId(),
      date: dateStr,
      month: dateStr.slice(0,7),
      channel: "B2B",
      description: client,
      situacao: String(sitRaw||"").trim(),
      expectedRevenue: value,
      probability: 0.95,
      source: "Omie B2B",
    });
  }
  return projs;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════
const fmt   = n => new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(n||0);
const fmtC  = n => new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL",notation:"compact",maximumFractionDigits:1}).format(n||0);
const fmtP  = n => (n||0).toFixed(1)+"%";
const toB64 = f => new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(f);});
const txKey = t => (t.companyId||"default")+"|"+t.date+"|"+t.source+"|"+t.amount+"|"+(t.description||"").slice(0,30);
const ym    = d => d ? String(d).slice(0,7) : "";
const parseBRL = s => { if (!s || String(s).trim()==="-") return 0; return parseFloat(String(s).trim().replace(/\./g,"").replace(",","."))||0; };
const genId = () => "id_"+Date.now()+"_"+Math.random().toString(36).slice(2,7);

// ═══════════════════════════════════════════════════════════════
// PARSERS
// ═══════════════════════════════════════════════════════════════
function parseMPXLSX(buf) {
  const wb = XLSX.read(buf, {type:"array", cellDates:true});
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, {header:1, defval:""});
  if (!rows.length) return {transactions:[], balanceByDate:{}, needsTransferReview:[]};

  // Detect format: new detailed report (has RELEASE_DATE header) vs old format
  const headerRow = rows[0].map(h => String(h||"").toUpperCase().trim());
  const isNewFormat = headerRow.includes("RELEASE_DATE") || headerRow.includes("NET_CREDIT_AMOUNT");

  if (isNewFormat) {
    return parseMPDetailedReport(rows, headerRow);
  } else {
    return parseMPLegacy(rows);
  }
}

function parseMPDetailedReport(rows, headerRow) {
  const ci = key => headerRow.findIndex(h => h === key || h.includes(key));
  const cReleaseDate = ci("RELEASE_DATE");
  const cExtRef      = ci("EXTERNAL_REFERENCE");
  const cRecordType  = ci("RECORD_TYPE");
  const cDesc        = ci("DESCRIPTION");
  const cNetCredit   = ci("NET_CREDIT_AMOUNT");
  const cNetDebit    = ci("NET_DEBIT_AMOUNT");
  const cGross       = ci("GROSS_AMOUNT");
  const cMpFee       = ci("MP_FEE_AMOUNT");
  const cFinFee      = ci("FINANCING_FEE_AMOUNT");
  const cApproval    = ci("APPROVAL_DATE");
  const cBalance     = ci("BALANCE");
  const cSaleDetail  = ci("SALE_DETAIL");

  const parseNum = v => {
    if (v === null || v === undefined || v === "") return 0;
    return parseFloat(String(v).replace(/[^\d\-\.]/g,"")) || 0;
  };
  const parseDate = v => {
    if (!v) return "";
    const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  };
  const cleanDetail = v => {
    if (!v) return "";
    return String(v).replace(new RegExp(String.fromCharCode(34),"g"),"").trim().slice(0,100);
  };

  const transactions = [];
  const balanceByDate = {};
  const needsTransferReview = [];

  const RESERVE_TYPES = new Set([
    "reserve_for_dispute", "reserve for refund",
    "reserve for payment", "reserve for payout",
  ]);

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const recordType = String(row[cRecordType] || "").trim();
    const desc       = String(row[cDesc] || "").trim();
    const descLow    = desc.toLowerCase();

    if (recordType === "Total") continue;

    if (recordType === "Initial available balance") {
      const credit = parseNum(row[cNetCredit]);
      const rd = parseDate(row[cReleaseDate]);
      if (credit > 0 && rd) balanceByDate[rd] = credit;
      continue;
    }

    const releaseDate    = parseDate(row[cReleaseDate]);
    const approvalDate   = parseDate(row[cApproval]);
    if (!releaseDate) continue;

    const extRef     = String(row[cExtRef] || "").trim();
    const netCredit  = parseNum(row[cNetCredit]);
    const netDebit   = parseNum(row[cNetDebit]);
    const gross      = parseNum(row[cGross]);
    const mpFee      = Math.abs(parseNum(row[cMpFee]));
    const finFee     = Math.abs(parseNum(row[cFinFee]));
    const balance    = parseNum(row[cBalance]);
    const saleDetail = cleanDetail(row[cSaleDetail]);

    // Always track the running account balance by release date
    if (balance !== 0) balanceByDate[releaseDate] = balance;

    // Skip reserve rows — they are internal MP account holds, not real cash flows
    if (RESERVE_TYPES.has(descLow)) continue;

    // tx.date = releaseDate so monthly grouping matches the extrato period
    // tx.competenceDate = approvalDate for future DRE-by-competence support
    const txBase = {
      releaseDate,
      competenceDate: approvalDate || releaseDate,
      reference: extRef,
      source: "Mercado Pago",
      accountName: "Mercado Pago",
      grossAmount: gross,
      balance,
      saleDetail,
    };

    if (descLow === "payment") {
      if (netCredit > 0) {
        const description = saleDetail
          ? saleDetail
          : extRef ? "Venda MP " + extRef.slice(-8) : "Recebimento MP";
        const isFrete = saleDetail.toLowerCase().includes("frete");
        transactions.push({
          ...txBase,
          id: genId(),
          date: releaseDate,
          description,
          transactionType: "Payment",
          fee: mpFee + finFee,
          type: "credit",
          amount: netCredit,
          category: isFrete ? "Frete" : "Receita de Vendas",
          needsReview: false,
        });
        if (mpFee + finFee > 0) {
          transactions.push({
            ...txBase,
            id: genId(),
            date: releaseDate,
            description: "Taxa MP - " + description.slice(0,40),
            transactionType: "MP Fee",
            fee: 0,
            type: "debit",
            amount: mpFee + finFee,
            category: "Tarifas e Taxas",
            needsReview: false,
          });
        }
      } else if (netDebit > 0) {
        const description = saleDetail
          ? saleDetail
          : extRef ? "Pagamento MP " + extRef.slice(-12) : "Pagamento MP";
        needsTransferReview.push({
          ...txBase,
          id: genId(),
          date: releaseDate,
          description,
          transactionType: "Payment (saída)",
          type: "debit",
          amount: netDebit,
          category: "Aguardando Classificação",
          needsReview: true,
        });
      }
    } else if (descLow === "refund" && netDebit > 0) {
      const description = saleDetail
        ? "Devolução: " + saleDetail.slice(0,60)
        : extRef ? "Devolução MP " + extRef.slice(-8) : "Devolução MP";
      transactions.push({
        ...txBase,
        id: genId(),
        date: releaseDate,
        description,
        transactionType: "Refund",
        type: "debit",
        amount: netDebit,
        category: "Devoluções",
        needsReview: false,
      });
    } else if (descLow === "cash withdrawal" && netDebit > 0) {
      transactions.push({
        ...txBase,
        id: genId(),
        date: releaseDate,
        description: "Transferência MP → Conta",
        transactionType: "Cash withdrawal",
        type: "debit",
        amount: netDebit,
        category: "Transferência Saída",
        needsReview: false,
      });
    }
  }

  return {transactions, balanceByDate, needsTransferReview};
}

function parseMPLegacy(rows) {
  // Original parser for old MP XLSX format (TRANSACTION_TYPE + PARTIAL_BALANCE columns)
  let hi = 0;
  for (let i=0; i<Math.min(rows.length,20); i++) {
    const r = rows[i].map(c=>String(c).toLowerCase());
    if (r.some(c=>c.includes("date")||c.includes("data")||c.includes("transaction"))) { hi=i; break; }
  }
  const hdr = rows[hi].map(h=>String(h).toLowerCase().trim().replace(/\s+/g,"_"));
  const ci  = ks => hdr.findIndex(h=>ks.some(k=>h.includes(k)));
  const cDate  = ci(["date","data","fecha"]);
  const cDesc  = ci(["description","descri","detalle","detalhe"]);
  const cType  = ci(["transaction_type","type","tipo","operação","natureza"]);
  const cAmt   = ci(["amount","valor","monto","net_credit","net_debit"]);
  const cBal   = ci(["partial_balance","balance","saldo","running_balance"]);
  const cGross = ci(["gross_amount","valor_bruto"]);
  const cFee   = ci(["fee","tarifa","commission"]);
  const cRef   = ci(["external_reference","referencia","order_id","payment_id"]);
  const cStatus= ci(["status","situação"]);

  const transactions = [], balanceByDate = {}, needsTransferReview = [];

  rows.slice(hi+1).filter(r=>r.some(c=>c!=="")).forEach((row)=>{
    let raw=cDate>=0?row[cDate]:"", ds="";
    if (raw instanceof Date) ds=raw.toISOString().slice(0,10);
    else { const s=String(raw),p=s.split(/[\/\-\.]/); if(p.length===3) ds=p[0].length===4?p[0]+"-"+p[1].padStart(2,"0")+"-"+p[2].padStart(2,"0"):p[2]+"-"+p[1].padStart(2,"0")+"-"+p[0].padStart(2,"0"); else ds=s; }
    if (!ds||ds.length<8) return;
    const txType=String(cType>=0?row[cType]||"":""),desc=String(cDesc>=0?row[cDesc]||txType:"").slice(0,100),
      status=String(cStatus>=0?row[cStatus]||"":""),ref=String(cRef>=0?row[cRef]||"":""),
      gross=cGross>=0?parseBRL(row[cGross]):0,fee=cFee>=0?Math.abs(parseBRL(row[cFee])):0,
      balance=cBal>=0?parseBRL(row[cBal]):0;
    let val=cAmt>=0?row[cAmt]:0;
    val=typeof val==="number"?val:parseBRL(val);
    if (val===0&&gross!==0) val=gross;
    const absVal=Math.abs(val); if(absVal===0) return;
    const type=val>=0?"credit":"debit";
    const catFromType=mapMPTransactionType(txType),cat=catFromType||"Outros";
    if (balance!==0) balanceByDate[ds]=balance;
    const tx={id:genId(),date:ds,description:desc,transactionType:txType,status,reference:ref,grossAmount:gross,fee,balance,type,amount:absVal,category:cat,source:"Mercado Pago",needsReview:cat==="Aguardando Classificação"||cat==="Outros",accountName:"Mercado Pago"};
    if (cat==="Aguardando Classificação") needsTransferReview.push(tx); else transactions.push(tx);
  });
  return {transactions, balanceByDate, needsTransferReview};
}

async function parseOmiePDF(arrayBuffer) {
  if (!window.pdfjsLib) {
    await new Promise((res,rej)=>{
      const s=document.createElement("script");
      s.src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      s.onload=res; s.onerror=()=>rej(new Error("Falha pdf.js")); document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  }
  const pdf = await window.pdfjsLib.getDocument({data:arrayBuffer}).promise;
  let txt = "";
  for (let p=1; p<=pdf.numPages; p++) {
    const pg=await pdf.getPage(p), ct=await pg.getTextContent(); let ly=null;
    for (const item of ct.items) { if(ly!==null&&Math.abs(item.transform[5]-ly)>3) txt+="\n"; txt+=item.str; ly=item.transform[5]; } txt+="\n";
  }
  let refY = new Date().getFullYear();
  const pm = txt.match(/[Pp]er[íi]odo.*?(\d{4})/); if(pm) refY=parseInt(pm[1]);

  const KCATS = ["Receitas B2B","Tarifas Bancárias","BPO Financeiro","Devoluções de Frete","Serviço de Terceiro PJ","Pró-labore","Conservação e Limpeza","Conservação e Limpeza - Filial SP","Frete","Marketing","Ferramentas de Marketing","Energia Elétrica - Filial SP","Internet","Contabilidade","Contabilidade - Filial SP","Contabilidade - Prime Ind","Aluguel - Filial SP","Coworking - Filial SP","ICMS","ICMS - Difal","INSS Sócio Titular","PIS (17,807978%); COFINS (82,192022%)","IRPJ (52,631614%); CSLL (47,368386%)","Sistemas e Softwares","Sistemas e Softwares - Filial SP","Sistemas e Softwares - Prime Ind","Compras de Mercadorias para Revenda","Entrada de Transferência","Embalagens","Custos Aduaneiros Importação","Taxa de Registro (Estadual)","Segurança e Vigilância - Filial SP","Água e Esgoto - Filial SP","Tráfego pago","Seguros"];

  const rawLines = txt.replace(/\r/g,"").split("\n").map(l=>l.trim()).filter(Boolean);
  const logicals = []; let buf = "";
  for (const line of rawLines) {
    if (/^(Conciliado|Pendente|Previsto)\s+\d{2}\/\d{2}\s/i.test(line)) { if(buf) logicals.push(buf); buf=line; } else if(buf) buf+=" "+line;
  }
  if(buf) logicals.push(buf);

  const BRL = /\d{1,3}(?:\.\d{3})*,\d{2}/;
  const txs = []; const balanceByDate = {}; let ix = 0;
  for (const line of logicals) {
    const hm = line.match(/^(Conciliado|Pendente|Previsto)\s+(\d{2})\/(\d{2})\s+(.+)$/i);
    if(!hm) continue;
    const [,situacao,day,month,rest] = hm;
    const date = refY+"-"+month.padStart(2,"0")+"-"+day.padStart(2,"0");
    const vr = new RegExp("("+BRL.source+"|-)"+"\\s+"+"("+BRL.source+"|-)"+"\\s+"+"("+BRL.source+")\\s*$");
    const vm = rest.match(vr); if(!vm) continue;
    const ent=parseBRL(vm[1]), sai=parseBRL(vm[2]), saldo=parseBRL(vm[3]);
    if(ent===0&&sai===0) continue;
    const bv = rest.slice(0,rest.length-vm[0].length).trim();
    const docMatch = bv.match(/\b(tar|pix|bol|doc|ref)\.\d{8,}\.\d+/i);
    const docNum = docMatch ? docMatch[0] : "";
    let omiecat="", desc=bv;
    for (const cat of KCATS) { const i=bv.indexOf(cat); if(i!==-1){omiecat=cat;desc=bv.slice(0,i).trim();break;} }
    desc = desc.replace(/\s+(?:tar|pix|bol|doc|ref)\.\d{8,}\.\d+/gi,"").replace(/\s+ok\b/gi,"").replace(/\s+ok\s*$/i,"").trim();
    const type=ent>0?"credit":"debit", amount=ent>0?ent:sai;
    const cat = mapOmieCat(omiecat, desc);
    if(saldo>0) balanceByDate[date]=saldo;
    txs.push({
      id:genId(), date, description:(desc||bv).slice(0,100),
      omieCategory:omiecat||"-", document:docNum, situacao, balance:saldo,
      type, amount, category:cat, source:"Omie Cash", accountName:"Omie Cash",
      needsReview:cat==="Outros",
    });
  }
  if(!txs.length) throw new Error("Nenhuma transação encontrada. Verifique se é um extrato Omie Cash válido.");
  return {transactions:txs, balanceByDate};
}

async function parseImageAPI(b64, mt) {
  const validTypes = ["image/jpeg","image/png","image/gif","image/webp"];
  const mediaType = validTypes.includes(mt) ? mt : "image/jpeg";

  // Build system prompt without problematic chars
  const fmtExample = "projections array com: date(YYYY-MM-DD), description, expectedRevenue(numero), probability(0.95=liberado/0.85=pendente/0.70=futuro), channel, orders";
  const imgSys = "Extrator de recebimentos futuros do Mercado Pago. Analise a imagem e retorne SOMENTE JSON valido sem markdown, sem texto extra. O JSON deve ter uma chave projections que e um array de objetos com os campos: "+fmtExample+". Inclua todos os recebimentos visiveis na imagem.";

  const r = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
    body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:2000,system:imgSys,
      messages:[{role:"user",content:[
        {type:"image",source:{type:"base64",media_type:mediaType,data:b64}},
        {type:"text",text:"Extraia todos os recebimentos futuros desta imagem. Retorne apenas o JSON com a chave projections."}
      ]}]})});
  const raw = await r.text();
  let d;
  try { d = JSON.parse(raw); }
  catch(e) { throw new Error("Resposta invalida da API: "+raw.slice(0,100)); }
  if(d.error) throw new Error("Erro API: "+d.error.message);
  const text = (d.content||[]).find(b=>b.type==="text")?.text||"";
  if(!text) throw new Error("API retornou resposta vazia. Tente novamente ou use imagem mais nitida.");
  // Extract JSON safely - find first { to last }
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if(start===-1||end===-1) throw new Error("Nenhum JSON na resposta. IA disse: "+text.slice(0,100));
  const parsed = JSON.parse(text.slice(start, end+1));
  const projections = parsed.projections||[];
  if(!projections.length) throw new Error("Nenhum recebimento identificado. Certifique-se que a imagem mostra a tela de recebimentos futuros do Mercado Pago.");
  return projections;
}

async function suggestCatAPI(desc, txType, omieCategory, brief) {
  const sysPrompt = "Classifique este lancamento em UMA categoria. "+(brief?"Contexto: "+brief+". ":"")+"Categorias: "+CATEGORIES.join(", ")+". Responda SOMENTE JSON sem markdown com os campos category, reason e confidence.";
  const userContent = "Classifique: \""+desc+"\"" +(txType?" | TRANSACTION_TYPE: "+txType:"")+(omieCategory&&omieCategory!=="-"?" | Omie: "+omieCategory:"");
  const r = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
    body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:150,system:sysPrompt,messages:[{role:"user",content:userContent}]})});
  const raw=await r.text(); let d; try{d=JSON.parse(raw);}catch{throw new Error("API invalida");}
  if(d.error) throw new Error(d.error.message);
  const text=(d.content||[]).find(b=>b.type==="text")?.text||"{}";
  return JSON.parse(text.replace(/```json|```/g,"").trim());
}

async function bulkCategorizeAPI(items, brief) {
  const bulkSys = "Classifique lançamentos financeiros. "+(brief?"Empresa: "+brief+". ":"")+"Categorias: "+CATEGORIES.join(", ")+". Responda SOMENTE JSON sem markdown com campo results sendo array de objetos com index, category e confidence.";
  const r = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
    body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:2000,system:bulkSys,messages:[{role:"user",content:"Classifique:\n"+items}]})});
  const raw=await r.text(); let d; try{d=JSON.parse(raw);}catch{throw new Error("API invalida");}
  if(d.error) throw new Error(d.error.message);
  const text=(d.content||[]).find(b=>b.type==="text")?.text||"{}";
  const parsed=JSON.parse(text.replace(/```json|```/g,"").trim());
  return parsed.results||[];
}

// ═══════════════════════════════════════════════════════════════
// DATA PROCESSING
// ═══════════════════════════════════════════════════════════════
function buildCashRunway(balanceByDate, projs, txs) {
  const dates = Object.keys(balanceByDate).sort();
  if (!dates.length) return null;
  const combinedBalance = {};
  dates.forEach(date=>{
    const entry = balanceByDate[date];
    if (typeof entry==="object") {
      const total = Object.values(entry).reduce((a,b)=>a+b,0);
      combinedBalance[date] = total;
    } else if (typeof entry==="number") {
      combinedBalance[date] = entry;
    }
  });
  const sortedDates = Object.keys(combinedBalance).sort();
  const lastDate = sortedDates[sortedDates.length-1];
  const lastBalance = combinedBalance[lastDate];
  const cutoff = new Date(lastDate); cutoff.setDate(cutoff.getDate()-30);
  const cutoffStr = cutoff.toISOString().slice(0,10);
  const recentTxs = txs.filter(t=>t.date>=cutoffStr&&t.date<=lastDate);
  const opexTotal = recentTxs.filter(t=>t.type==="debit"&&OPEX_CATS.includes(t.category)).reduce((a,t)=>a+t.amount,0);
  const revenueTotal = recentTxs.filter(t=>t.type==="credit"&&(t.category==="Receita de Vendas"||t.category==="Outras Receitas")).reduce((a,t)=>a+t.amount,0);
  const dayCount = 30;
  const avgDailyBurn = opexTotal/dayCount;
  const avgDailyIncome = revenueTotal/dayCount;
  const avgDailyNet = avgDailyIncome-avgDailyBurn;
  const futureByDate = {};
  projs.forEach(p=>{ if(p.date>lastDate){ futureByDate[p.date]=(futureByDate[p.date]||0)+(p.expectedRevenue||0)*(p.probability||0.85); } });
  const forecast = []; let runBal = lastBalance; let zeroDay = null;
  const startDate = new Date(lastDate); startDate.setDate(startDate.getDate()+1);
  for (let i=0; i<90; i++) {
    const ds = new Date(startDate.getTime()+i*86400000).toISOString().slice(0,10);
    const incoming = futureByDate[ds]||0;
    runBal += avgDailyNet+incoming;
    if (runBal<0&&!zeroDay) zeroDay=ds;
    forecast.push({date:ds,balance:Math.max(0,runBal),incoming,avgDailyBurn,avgDailyIncome,isProjected:true});
    if (zeroDay&&i>5) break;
  }
  const historical = sortedDates.slice(-30).map(ds=>({date:ds,balance:combinedBalance[ds],isProjected:false}));
  return {historical,forecast,lastDate,lastBalance,avgDailyBurn,avgDailyIncome,avgDailyNet,zeroDay,combinedBalance};
}

function processData(txs, projs, cmvByPeriod, selectedPeriod, balanceByDate, companyFilter) {
  // STRICT company filter — no leaking across companies
  const cFilter = companyFilter||"default";
  const companyTxs = txs.filter(t=>(t.companyId||"default")===cFilter);
  // DRE usa competenceDate (col S = data aprovação); fluxo de caixa usa date (data liberação)
  const filteredTxs = selectedPeriod ? companyTxs.filter(t=>ym(t.competenceDate||t.date)===selectedPeriod) : companyTxs;
  let cmvManual = 0;
  if (cmvByPeriod.length>0) {
    const match = selectedPeriod ? cmvByPeriod.find(c=>c.period===selectedPeriod) : cmvByPeriod[cmvByPeriod.length-1];
    if (match) cmvManual = match.value;
  }
  let rev=0, cogs=0, pers=0, mkt=0, fix=0, ops=0, ret=0, frete=0, fees=0;
  const catTxMap = {};
  const subCatMap = {};
  filteredTxs.forEach(t=>{
    if(t.category==="Transferência Entrada"||t.category==="Transferência Saída"||t.category==="Aguardando Classificação") return;
    const addTo=(cat)=>{ if(!catTxMap[cat])catTxMap[cat]=[]; catTxMap[cat].push(t); if(!subCatMap[cat])subCatMap[cat]={}; const sub=t.omieCategory||t.transactionType||t.description.slice(0,40); if(!subCatMap[cat][sub])subCatMap[cat][sub]=[]; subCatMap[cat][sub].push(t); };
    if(t.type==="credit"){ if(t.category==="Receita de Vendas"||t.category==="Outras Receitas"){ rev+=(t.grossAmount||t.amount); addTo("Receita de Vendas"); } }
    else {
      if(t.category==="Custo de Mercadoria"){cogs+=t.amount;addTo("Custo de Mercadoria");}
      else if(t.category==="Despesas com Pessoal"){pers+=t.amount;addTo("Despesas com Pessoal");}
      else if(t.category==="Marketing"){mkt+=t.amount;addTo("Marketing");}
      else if(t.category==="Despesas Fixas"){fix+=t.amount;addTo("Despesas Fixas");}
      else if(t.category==="Despesas Operacionais"){ops+=t.amount;addTo("Despesas Operacionais");}
      else if(t.category==="Tarifas e Taxas"){fees+=t.amount;addTo("Tarifas e Taxas");}
      else if(t.category==="Frete"){frete+=t.amount;addTo("Frete");}
      else if(t.category==="Devoluções"){ret+=t.amount;addTo("Devoluções");}
      else if(t.category==="Outros"){ops+=t.amount;addTo("Despesas Operacionais");}
    }
  });
  const finalCogs=cmvManual>0?cmvManual:cogs;
  const netRev=rev-ret, gProfit=netRev-finalCogs, gMargin=netRev>0?(gProfit/netRev)*100:0;
  const opex=pers+mkt+fix+ops+fees+frete, ebitda=gProfit-opex, ebitdaM=netRev>0?(ebitda/netRev)*100:0;
  // cf uses ALL company transactions for the monthly chart
  const cf = {};
  companyTxs.forEach(t=>{ const m=ym(t.date)||"s/data"; if(!cf[m])cf[m]={inflow:0,outflow:0}; t.type==="credit"?(cf[m].inflow+=(t.grossAmount||t.amount)):(cf[m].outflow+=t.amount); }); // cf usa t.date (releaseDate); entrada usa grossAmount (bruto)
  const pj = {};
  const companyProjs = projs.filter(p=>(p.companyId||"default")===(companyFilter||"default"));
  companyProjs.forEach(p=>{ const k=p.month||(p.date?ym(p.date):"Futuro"); if(!pj[k])pj[k]={projected:0,weighted:0}; pj[k].projected+=p.expectedRevenue||0; pj[k].weighted+=(p.expectedRevenue||0)*(p.probability||0.85); });
  const uncategorized = companyTxs.filter(t=>t.needsReview||t.category==="Outros"||t.category==="Aguardando Classificação").length;
  // Filter balanceByDate to only this company
  const companyBalByDate = {};
  Object.entries(balanceByDate).forEach(([date,entry])=>{
    if(typeof entry==="object"){
      const filtered = Object.fromEntries(Object.entries(entry).filter(([k])=>k.endsWith(":::"+companyFilter)));
      if(Object.keys(filtered).length>0) companyBalByDate[date]=filtered;
    }
  });
  // Tarifas MP já vêm como lançamentos debit separados com competenceDate correto
  const runway = buildCashRunway(companyBalByDate, companyProjs, companyTxs);
  // Compute total cash balance filtered by company
  const latestBalances = {};
  const sortedBalDates = Object.keys(balanceByDate).sort();
  sortedBalDates.forEach(date=>{
    const entry = balanceByDate[date];
    if(typeof entry==="object"&&entry!==null){
      Object.entries(entry).forEach(([key,bal])=>{
        const parts = key.split(":::");
        const srcCompany = parts.length>1?parts[1]:"default";
        const srcName = parts[0];
        if(bal>0&&(srcCompany===cFilter||srcCompany==="default")) latestBalances[srcName]=bal;
      });
    }
  });
  // Note: latestBalances comes only from balanceByDate (sequentially per date)
  // tx.balance is intentionally excluded: tx.date = approvalDate (past) != releaseDate
  const totalCashBalance = Object.values(latestBalances).reduce((a,b)=>a+b,0);
  return {dre:{grossRev:rev,ret,netRev,cogs:finalCogs,cogsAuto:cogs,cmvManual,gProfit,gMargin,pers,mkt,fix,ops,fees,frete,opex,ebitda,ebitdaM},cf,pj,txs:filteredTxs,allTxs:companyTxs,projs:companyProjs,uncategorized,catTxMap,subCatMap,runway,totalCashBalance,latestBalances};
}


function KPI({label,value,sub,color,icon,highlight,onClick}){
  return(
    <div onClick={onClick} style={{background:B.card,border:"1px solid "+(highlight?color+"44":B.border),borderRadius:12,padding:"clamp(10px,1.5vw,14px)",flex:1,minWidth:0,boxShadow:"0 1px 4px rgba(0,0,0,0.07)",cursor:onClick?"pointer":"default"}}>
      <div style={{fontSize:"clamp(9px,1vw,10px)",color:B.textMut,fontWeight:600,letterSpacing:"0.05em",marginBottom:4}}>{icon} {label}</div>
      <div style={{fontSize:"clamp(15px,2vw,20px)",fontWeight:800,color:color||B.dark,letterSpacing:"-0.03em",lineHeight:1.1,wordBreak:"break-word"}}>{value}</div>
      {sub&&<div style={{fontSize:"clamp(9px,1vw,11px)",color:B.textSub,marginTop:4}}>{sub}</div>}
    </div>
  );
}

function Card({title,children,noPad,badge,accent,onClick}){
  return(
    <div onClick={onClick} style={{background:B.card,border:"1px solid "+B.border,borderLeft:accent?"4px solid "+accent:undefined,borderRadius:12,marginBottom:12,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",cursor:onClick?"pointer":"default"}}>
      {title&&(<div style={{padding:"clamp(10px,1.2vw,13px) clamp(12px,1.8vw,16px)",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+B.border}}>
        <div style={{fontSize:"clamp(10px,1.1vw,12px)",color:B.textSub,fontWeight:700,letterSpacing:"0.08em"}}>{title}</div>
        {badge&&<span style={{fontSize:9,background:B.red+"18",color:B.red,border:"1px solid "+B.red+"44",padding:"2px 8px",borderRadius:20,fontWeight:700}}>{badge}</span>}
      </div>)}
      <div style={noPad?{}:{padding:title?"clamp(10px,1.2vw,13px) clamp(12px,1.8vw,16px)":"clamp(12px,1.8vw,16px)"}}>{children}</div>
    </div>
  );
}

function Pill({label,color,selected,onClick}){
  return <button onClick={onClick} style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid "+(selected?color:B.border2),background:selected?color+"18":B.card,color:selected?color:B.textSub,fontSize:"clamp(10px,1.1vw,12px)",fontWeight:selected?700:400,cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.15s"}}>{label}</button>;
}

function MiniBar({data,c1,c2}){
  c1=c1||B.blue; c2=c2||B.red;
  const keys=Object.keys(data).sort(); if(!keys.length) return null;
  const [tip,setTip]=useState(null);
  const maxV=Math.max(...keys.flatMap(k=>[data[k].inflow||data[k].projected||0,data[k].outflow||data[k].weighted||0]),1);
  const H=80,W=300,pad=14,bw=Math.min(18,(W-pad*2)/(keys.length*2.8));
  const fmtTip=n=>n>=1000000?("R$ "+(n/1000000).toFixed(1)+"M"):n>=1000?("R$ "+(n/1000).toFixed(0)+"k"):("R$ "+n.toFixed(0));
  return(
    <div style={{position:"relative"}}>
      {tip&&<div style={{position:"absolute",top:0,left:tip.x,transform:"translateX(-50%)",background:"#1a1a2e",color:"white",padding:"4px 9px",borderRadius:6,fontSize:10,whiteSpace:"nowrap",zIndex:10,pointerEvents:"none",boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
        <div style={{fontWeight:700,marginBottom:2}}>{tip.label}</div>
        <div style={{color:c1}}>{tip.l1}: {fmtTip(tip.v1)}</div>
        {tip.v2>0&&<div style={{color:c2}}>{tip.l2}: {fmtTip(tip.v2)}</div>}
      </div>}
      <svg viewBox={"0 0 "+W+" "+(H+22)} style={{width:"100%",overflow:"visible"}} onMouseLeave={()=>setTip(null)}>
        {keys.map((k,i)=>{
          const x=pad+i*((W-pad*2)/keys.length),v1=data[k].inflow||data[k].projected||0,v2=data[k].outflow||data[k].weighted||0,h1=(v1/maxV)*H,h2=(v2/maxV)*H;
          const l1=data[k].inflow!==undefined?"Entradas":"Projetado";
          const l2=data[k].outflow!==undefined?"Saídas":"Ponderado";
          return(<g key={k} style={{cursor:"pointer"}}
            onMouseEnter={()=>setTip({x:((x+bw)/W*100)+"%",label:k,v1,v2,l1,l2})}>
            <rect x={x} y={H-h1} width={bw} height={h1} fill={c1} rx={2} opacity={!tip||tip.label===k?1:0.6}/>
            <rect x={x+bw+2} y={H-h2} width={bw} height={h2} fill={c2} rx={2} opacity={!tip||tip.label===k?1:0.6}/>
            <text x={x+bw} y={H+15} textAnchor="middle" fill={B.textMut} fontSize={8}>{String(k).slice(-5)}</text>
          </g>);
        })}
        <line x1={pad} y1={H} x2={W-4} y2={H} stroke={B.border2} strokeWidth={1}/>
      </svg>
    </div>
  );
}

function Donut({pct,label,color,size,value}){
  size=size||68; const r=26,c=34,circ=2*Math.PI*r,p=Math.min(Math.max(pct||0,0),100);
  const [show,setShow]=useState(false);
  return(
    <div style={{textAlign:"center",position:"relative",cursor:"default"}}
      onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
      {show&&value&&<div style={{position:"absolute",bottom:"105%",left:"50%",transform:"translateX(-50%)",background:"#1a1a2e",color:"white",padding:"3px 8px",borderRadius:5,fontSize:10,whiteSpace:"nowrap",zIndex:20,pointerEvents:"none"}}>{value}</div>}
      <svg viewBox="0 0 68 68" style={{width:size}}>
        <circle cx={c} cy={c} r={r} fill="none" stroke={B.border2} strokeWidth={7}/>
        <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth={7} strokeDasharray={circ*p/100+" "+circ} strokeLinecap="round" transform={"rotate(-90 "+c+" "+c+")"}/>
        <text x={c} y={c+4} textAnchor="middle" fill={B.dark} fontSize={11} fontWeight="700">{p.toFixed(0)}%</text>
      </svg>
      <div style={{fontSize:9,color:B.textMut,marginTop:2,fontWeight:600}}>{label}</div>
    </div>
  );
}

function PeriodFilter({months, selected, onChange}){
  if(!months.length) return null;
  return(
    <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,flexWrap:"wrap",marginBottom:8}}>
      <Pill label="Todos" selected={!selected} color={B.blue} onClick={()=>onChange("")}/>
      {months.map(m=>{ const [y,mo]=m.split("-"); return <Pill key={m} label={mo+"/"+y} selected={selected===m} color={B.blue} onClick={()=>onChange(m)}/>; })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DRE ROW with sub-category drill-down
// ═══════════════════════════════════════════════════════════════
function DRERow({label,value,sub,total,highlight,neg,catKey,subCatMap,catTxMap}){
  const [openLevel,setOpenLevel] = useState(0); // 0=closed, 1=subcats, 2=transactions
  const subCats = catKey && subCatMap ? subCatMap[catKey] : null;
  const allTxs  = catKey && catTxMap  ? catTxMap[catKey]  : null;
  const hasDrill = allTxs && allTxs.length > 0;

  const toggleOpen = () => {
    if (!hasDrill) return;
    if (subCats && Object.keys(subCats).length > 1) {
      setOpenLevel(prev => prev===0?1:prev===1?2:0);
    } else {
      setOpenLevel(prev => prev===0?2:0);
    }
  };

  const levelLabel = openLevel===0 ? "▼" : openLevel===1 ? "▲ subcats" : "▲ lançamentos";

  return(
    <>
      <div onClick={hasDrill?toggleOpen:undefined} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"clamp(9px,1.2vw,12px) clamp(12px,1.8vw,16px)",borderBottom:"1px solid "+B.border,background:highlight?"#E8F5E9":total?"#F8F9FA":"white",borderLeft:highlight?"3px solid "+B.green:total?"3px solid "+B.border2:"3px solid transparent",cursor:hasDrill?"pointer":"default"}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:total?"clamp(11px,1.3vw,14px)":"clamp(10px,1.2vw,13px)",fontWeight:total?700:400,color:neg&&value<0?B.red:total?B.dark:B.textSub}}>{label}</span>
            {hasDrill&&<span style={{fontSize:9,color:B.blue,border:"1px solid "+B.blue+"44",borderRadius:10,padding:"0 5px",fontWeight:600}}>{levelLabel} {allTxs.length}</span>}
          </div>
          {sub&&<div style={{fontSize:10,color:B.textMut,marginTop:2}}>{sub}</div>}
        </div>
        <div style={{fontFamily:"monospace",fontSize:total?"clamp(12px,1.4vw,15px)":"clamp(11px,1.2vw,13px)",fontWeight:total?700:400,color:neg&&value<0?B.red:highlight?B.green:total?B.dark:B.textSub,marginLeft:8,flexShrink:0}}>{fmt(value)}</div>
      </div>

      {/* Level 1: Sub-categories */}
      {openLevel>=1 && subCats && Object.keys(subCats).length > 0 && (
        <div style={{background:"#F0F4FF",borderBottom:"1px solid "+B.border}}>
          {Object.entries(subCats).sort(([,a],[,b])=>b.reduce((s,t)=>s+t.amount,0)-a.reduce((s,t)=>s+t.amount,0)).map(([subName,subTxs])=>{
            const subTotal = subTxs.reduce((s,t)=>s+t.amount,0);
            return(
              <div key={subName} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 28px",borderBottom:"1px solid "+B.border+"88"}}>
                <div>
                  <div style={{fontSize:11,color:B.text,fontWeight:500}}>{subName}</div>
                  <div style={{fontSize:9,color:B.textMut}}>{subTxs.length} lançamento{subTxs.length!==1?"s":""}</div>
                </div>
                <div style={{fontFamily:"monospace",fontSize:12,fontWeight:600,color:B.dark}}>{fmt(subTotal)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Level 2: Individual transactions */}
      {openLevel>=2 && allTxs && allTxs.length > 0 && (
        <div style={{background:"#F8FAFE",borderBottom:"1px solid "+B.border,maxHeight:280,overflowY:"auto"}}>
          {allTxs.map((t,i)=>(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 24px",borderBottom:i<allTxs.length-1?"1px solid "+B.border:"none"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:11,color:B.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:500}}>{t.description}</div>
                <div style={{fontSize:9,color:B.textMut,marginTop:1,display:"flex",gap:8,flexWrap:"wrap"}}>
                  <span>{t.date}</span><span>{t.source}</span>
                  {t.transactionType&&<span style={{color:B.blue}}>MP: {t.transactionType}</span>}
                  {t.omieCategory&&t.omieCategory!=="-"&&<span style={{color:"#9C27B0"}}>Omie: {t.omieCategory}</span>}
                </div>
              </div>
              <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:t.type==="credit"?B.green:B.red,flexShrink:0}}>{t.type==="credit"?"+":"-"}{fmt(t.type==="credit"?(t.grossAmount||t.amount):t.amount)}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRANSFER REVIEW MODAL — asks user to classify outbound transfers
// ═══════════════════════════════════════════════════════════════
function TransferReviewModal({transfers, onDone}){
  const [decisions, setDecisions] = useState({});
  const [idx, setIdx] = useState(0);

  if (!transfers || transfers.length === 0) return null;
  const current = transfers[idx];
  const decide = (cat) => {
    const next = {...decisions, [current.id]: cat};
    setDecisions(next);
    if (idx < transfers.length-1) setIdx(idx+1);
    else onDone(next);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div style={{width:"100%",maxWidth:520,background:B.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 32px",boxShadow:"0 -4px 20px rgba(0,0,0,0.15)"}}>
        <div style={{fontSize:13,fontWeight:800,color:B.dark,marginBottom:4}}>🔄 Classifique esta transferência</div>
        <div style={{fontSize:11,color:B.textMut,marginBottom:14}}>{idx+1} de {transfers.length}</div>
        <div style={{background:B.bg,borderRadius:10,padding:"12px 14px",marginBottom:16}}>
          <div style={{fontSize:13,color:B.text,fontWeight:600}}>{current.description}</div>
          <div style={{fontSize:11,color:B.textMut,marginTop:3}}>{current.date} · MP: {current.transactionType} · {fmt(current.amount)}</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {[
            {label:"💸 Transferência interna",cat:"Transferência Saída",color:B.textSub},
            {label:"📦 Pagamento Fornecedor",cat:"Custo de Mercadoria",color:"#E67C00"},
            {label:"👥 Folha / Pagto Pessoal",cat:"Despesas com Pessoal",color:B.blue},
            {label:"📢 Marketing / Publicidade",cat:"Marketing",color:"#9C27B0"},
            {label:"🏠 Despesa Fixa",cat:"Despesas Fixas",color:"#1565C0"},
            {label:"⚙️ Despesa Operacional",cat:"Despesas Operacionais",color:B.textSub},
            {label:"🚚 Frete / Logística",cat:"Frete",color:B.yellow},
            {label:"❓ Outro",cat:"Outros",color:B.textMut},
          ].map(({label,cat,color})=>(
            <button key={cat} onClick={()=>decide(cat)} style={{padding:"10px 8px",borderRadius:10,border:"1.5px solid "+B.border,background:B.card,color,fontSize:11,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CATEGORY MANAGER COMPONENT
// ═══════════════════════════════════════════════════════════════
function CategoryManager({company, companies, setCompanies, activeCompany, txs, brief, addMsg}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState("#5F6368");
  const [newCatType, setNewCatType] = useState("debit");
  const [newSubName, setNewSubName] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);

  const cats = getCompanyCategories(company);

  const updateCompany = (fn) => setCompanies(prev=>prev.map(c=>c.id===activeCompany?fn(c):c));

  const addCategory = () => {
    if (!newCatName.trim()) return;
    const newCat = {id:genId(), name:newCatName.trim(), color:newCatColor, type:newCatType, subcategories:[]};
    updateCompany(c=>({...c, categories:[...(c.categories||[...DEFAULT_CATEGORIES]), newCat]}));
    setNewCatName(""); setNewCatColor("#5F6368"); setNewCatType("debit");
  };

  const removeCategory = (catId) => {
    updateCompany(c=>({...c, categories:(c.categories||DEFAULT_CATEGORIES).filter(x=>x.id!==catId)}));
  };

  const startEdit = (cat) => {
    setEditingId(cat.id||cat.name);
    setEditName(cat.name);
    setEditColor(cat.color);
  };

  const saveEdit = (catId) => {
    updateCompany(c=>({...c, categories:(c.categories||DEFAULT_CATEGORIES).map(x=>(x.id||x.name)===catId?{...x,name:editName,color:editColor}:x)}));
    // Also update txs that had old category name
    setEditingId(null);
  };

  const addSubcategory = (catId, subName) => {
    if (!subName.trim()) return;
    updateCompany(c=>({...c, categories:(c.categories||DEFAULT_CATEGORIES).map(x=>(x.id||x.name)===catId?{...x,subcategories:[...(x.subcategories||[]),{id:genId(),name:subName.trim()}]}:x)}));
    setNewSubName(p=>({...p,[catId]:""}));
  };

  const removeSubcategory = (catId, subId) => {
    updateCompany(c=>({...c, categories:(c.categories||DEFAULT_CATEGORIES).map(x=>(x.id||x.name)===catId?{...x,subcategories:(x.subcategories||[]).filter(s=>s.id!==subId)}:x)}));
  };

  const suggestCategoriesAI = async () => {
    setAiLoading(true); setAiSuggestions([]);
    try {
      const cId = activeCompany||"default";
      const compTxs = txs.filter(t=>(t.companyId||"default")===cId).slice(0,60);
      const sampleDescs = [...new Set(compTxs.map(t=>t.description))].slice(0,40).join(", ");
      const existingCats = cats.map(c=>c.name).join(", ");
      const sys = "Você é um especialista em finanças empresariais. Analise os lançamentos financeiros de uma empresa e sugira novas categorias ou subcategorias que ainda não existem mas fariam sentido para organizar melhor os dados. Retorne SOMENTE JSON no formato: {suggestions:[{name:string, color:string(hex), type:'credit'|'debit'|'both', reason:string, isSubcategoryOf:string|null}]}. Máximo 5 sugestões. Cores hexadecimais variadas e distintas.";
      const prompt = "Empresa: "+(brief||"ecommerce")+".\nCategorias existentes: "+existingCats+".\nAmostras de lançamentos reais: "+sampleDescs+".\nSugira novas categorias/subcategorias que ainda não existem mas seriam úteis para esta empresa.";
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:800,system:sys,messages:[{role:"user",content:prompt}]})});
      const raw = await res.text(); let d; try{d=JSON.parse(raw);}catch{throw new Error("API inválida");}
      if(d.error) throw new Error(d.error.message);
      const text=(d.content||[]).find(b=>b.type==="text")?.text||"{}";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setAiSuggestions(parsed.suggestions||[]);
    } catch(e) { addMsg("assistant","⚠️ Erro ao consultar IA: "+e.message); }
    setAiLoading(false);
  };

  const applySuggestion = (sug) => {
    if (sug.isSubcategoryOf) {
      const parentId = cats.find(c=>c.name===sug.isSubcategoryOf)?.id || cats.find(c=>c.name===sug.isSubcategoryOf)?.name;
      if (parentId) { addSubcategory(parentId, sug.name); }
      else { // parent not found, add as main cat
        const newCat = {id:genId(), name:sug.name, color:sug.color, type:sug.type, subcategories:[]};
        updateCompany(c=>({...c, categories:[...(c.categories||[...DEFAULT_CATEGORIES]), newCat]}));
      }
    } else {
      const newCat = {id:genId(), name:sug.name, color:sug.color, type:sug.type, subcategories:[]};
      updateCompany(c=>({...c, categories:[...(c.categories||[...DEFAULT_CATEGORIES]), newCat]}));
    }
    setAiSuggestions(prev=>prev.filter(s=>s.name!==sug.name));
  };

  const resetToDefault = () => {
    if (window.confirm("Resetar para as categorias padrão? As categorias customizadas serão perdidas."))
      updateCompany(c=>({...c, categories:[...DEFAULT_CATEGORIES]}));
  };

  if (!activeCompany) return (
    <div style={{padding:"20px",textAlign:"center",color:B.textMut,fontSize:13}}>⚠️ Selecione uma empresa ativa nas Configurações para gerenciar categorias.</div>
  );

  const COLORS = ["#188038","#1A73E8","#D93025","#F9AB00","#9C27B0","#E67C00","#1565C0","#5F6368","#34A853","#78909C","#FF6F00","#00897B","#C62828","#283593"];

  return (
    <div style={{padding:"0 clamp(12px,2vw,20px) 32px"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,paddingTop:8,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:B.dark}}>🏷️ Categorias — {company?.name}</div>
          <div style={{fontSize:11,color:B.textMut,marginTop:2}}>{cats.length} categorias · {cats.reduce((a,c)=>a+(c.subcategories?.length||0),0)} subcategorias</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={suggestCategoriesAI} disabled={aiLoading} style={{background:aiLoading?"#9AA0A6":B.blue,border:"none",borderRadius:10,padding:"8px 14px",color:"white",fontSize:11,fontWeight:700,cursor:aiLoading?"default":"pointer"}}>
            {aiLoading?"⏳ Analisando...":"✨ IA Sugerir Novas"}
          </button>
          <button onClick={resetToDefault} style={{background:"transparent",border:"1px solid "+B.border2,borderRadius:10,padding:"8px 12px",color:B.textMut,fontSize:11,cursor:"pointer"}}>↺ Padrão</button>
        </div>
      </div>

      {/* AI Suggestions */}
      {aiSuggestions.length>0&&(
        <div style={{background:"#E3F2FD",border:"1px solid "+B.blue+"44",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:B.blue,marginBottom:10}}>✨ Sugestões da IA para {company?.name}</div>
          {aiSuggestions.map((sug,i)=>(
            <div key={i} style={{background:B.card,border:"1px solid "+B.border,borderRadius:10,padding:"10px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:sug.color,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:B.dark}}>{sug.name} <span style={{fontSize:9,color:B.textMut,background:B.bg,padding:"1px 5px",borderRadius:4}}>{sug.isSubcategoryOf?"subcategoria de "+sug.isSubcategoryOf:sug.type}</span></div>
                <div style={{fontSize:10,color:B.textSub,marginTop:2}}>{sug.reason}</div>
              </div>
              <button onClick={()=>applySuggestion(sug)} style={{background:B.green,border:"none",borderRadius:8,padding:"5px 12px",color:"white",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>+ Adicionar</button>
              <button onClick={()=>setAiSuggestions(prev=>prev.filter(s=>s.name!==sug.name))} style={{background:"transparent",border:"none",color:B.textMut,fontSize:16,cursor:"pointer",padding:"0 4px",flexShrink:0}}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Add new category */}
      <div style={{background:B.card,border:"1px solid "+B.border,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:B.textSub,letterSpacing:"0.08em",marginBottom:10}}>+ NOVA CATEGORIA</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
          <input value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="Nome da categoria" onKeyDown={e=>e.key==="Enter"&&addCategory()}
            style={{flex:1,minWidth:140,padding:"9px 12px",background:B.bg,border:"1px solid "+B.border2,borderRadius:8,fontSize:12,color:B.dark}}/>
          <select value={newCatType} onChange={e=>setNewCatType(e.target.value)}
            style={{padding:"9px 10px",background:B.bg,border:"1px solid "+B.border2,borderRadius:8,fontSize:12,color:B.dark}}>
            <option value="credit">Receita</option>
            <option value="debit">Despesa</option>
            <option value="both">Ambos</option>
          </select>
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {COLORS.map(c=>(
              <div key={c} onClick={()=>setNewCatColor(c)} style={{width:18,height:18,borderRadius:"50%",background:c,cursor:"pointer",border:newCatColor===c?"3px solid "+B.dark:"2px solid transparent",flexShrink:0}}/>
            ))}
          </div>
          <button onClick={addCategory} disabled={!newCatName.trim()} style={{background:newCatName.trim()?B.green:"#9AA0A6",border:"none",borderRadius:8,padding:"9px 16px",color:"white",fontSize:12,fontWeight:700,cursor:newCatName.trim()?"pointer":"default",flexShrink:0}}>Criar</button>
        </div>
      </div>

      {/* Category list */}
      {cats.map(cat=>{
        const cId = cat.id||cat.name;
        const isEditing = editingId===cId;
        const isExpanded = expandedCat===cId;
        return(
          <div key={cId} style={{background:B.card,border:"1.5px solid "+(isExpanded?cat.color:B.border),borderRadius:12,marginBottom:8,overflow:"hidden"}}>
            {/* Category row */}
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px"}}>
              <div style={{width:12,height:12,borderRadius:"50%",background:cat.color,flexShrink:0}}/>
              {isEditing ? (
                <div style={{flex:1,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <input value={editName} onChange={e=>setEditName(e.target.value)} autoFocus
                    style={{flex:1,minWidth:100,padding:"5px 8px",background:B.bg,border:"1px solid "+B.border2,borderRadius:6,fontSize:12,color:B.dark}}/>
                  <div style={{display:"flex",gap:3}}>
                    {COLORS.map(c=><div key={c} onClick={()=>setEditColor(c)} style={{width:16,height:16,borderRadius:"50%",background:c,cursor:"pointer",border:editColor===c?"3px solid "+B.dark:"2px solid transparent"}}/>)}
                  </div>
                  <button onClick={()=>saveEdit(cId)} style={{background:B.green,border:"none",borderRadius:6,padding:"4px 10px",color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>✓</button>
                  <button onClick={()=>setEditingId(null)} style={{background:"transparent",border:"1px solid "+B.border2,borderRadius:6,padding:"4px 8px",color:B.textMut,fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              ) : (
                <>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:B.dark}}>{cat.name}</div>
                    <div style={{fontSize:9,color:B.textMut,marginTop:1}}>{cat.type==="credit"?"Receita":cat.type==="debit"?"Despesa":"Ambos"} · {cat.subcategories?.length||0} subcategorias</div>
                  </div>
                  <button onClick={()=>setExpandedCat(isExpanded?null:cId)} style={{background:isExpanded?cat.color+"18":"transparent",border:"1px solid "+(isExpanded?cat.color:B.border),borderRadius:6,padding:"4px 10px",color:isExpanded?cat.color:B.textSub,fontSize:10,cursor:"pointer",fontWeight:600}}>
                    {isExpanded?"▲ Fechar":"▼ Subcats "+(cat.subcategories?.length>0?"("+cat.subcategories.length+")":"")}
                  </button>
                  <button onClick={()=>startEdit(cat)} style={{background:"transparent",border:"1px solid "+B.border,borderRadius:6,padding:"4px 8px",color:B.textSub,fontSize:10,cursor:"pointer"}}>✏️</button>
                  <button onClick={()=>removeCategory(cId)} style={{background:B.red+"12",border:"1px solid "+B.red+"33",borderRadius:6,padding:"4px 8px",color:B.red,fontSize:10,cursor:"pointer"}}>🗑️</button>
                </>
              )}
            </div>
            {/* Subcategories */}
            {isExpanded&&(
              <div style={{borderTop:"1px solid "+B.border,background:"#FAFAFA",padding:"10px 14px"}}>
                {(cat.subcategories||[]).length===0&&<div style={{fontSize:11,color:B.textMut,marginBottom:8}}>Nenhuma subcategoria ainda.</div>}
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                  {(cat.subcategories||[]).map(sub=>(
                    <div key={sub.id} style={{display:"flex",alignItems:"center",gap:4,background:cat.color+"15",border:"1px solid "+cat.color+"44",borderRadius:16,padding:"4px 10px"}}>
                      <span style={{fontSize:11,color:B.dark,fontWeight:500}}>{sub.name}</span>
                      <button onClick={()=>removeSubcategory(cId,sub.id)} style={{background:"transparent",border:"none",color:B.textMut,fontSize:12,cursor:"pointer",padding:"0 2px",lineHeight:1}}>✕</button>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <input value={newSubName[cId]||""} onChange={e=>setNewSubName(p=>({...p,[cId]:e.target.value}))} placeholder="Nova subcategoria..." onKeyDown={e=>e.key==="Enter"&&addSubcategory(cId,newSubName[cId]||"")}
                    style={{flex:1,padding:"7px 10px",background:B.card,border:"1px solid "+B.border2,borderRadius:8,fontSize:11,color:B.dark}}/>
                  <button onClick={()=>addSubcategory(cId,newSubName[cId]||"")} style={{background:cat.color,border:"none",borderRadius:8,padding:"7px 14px",color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>+ Add</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCREENS
// ═══════════════════════════════════════════════════════════════

// HOME SCREEN
function HomeScreen({data,months,period,setPeriod,uncategorized,onGoTo,appLogo}){
  if(!data||!data.allTxs.length) return(
    <div style={{padding:"60px 20px",textAlign:"center"}}>
      <div style={{fontSize:56,marginBottom:16}}>📊</div>
      <div style={{color:B.textSub,fontSize:15,lineHeight:1.7}}>Importe seus extratos na aba <strong style={{color:B.blue}}>Importar</strong> para começar</div>
    </div>
  );
  const {dre,cf,runway,totalCashBalance,latestBalances} = data;
  const totalProjected = Object.values(data.pj).reduce((a,b)=>a+b.projected,0);
  const totalWeighted = Object.values(data.pj).reduce((a,b)=>a+b.weighted,0);

  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      {uncategorized>0&&(<div onClick={()=>onGoTo("categorize")} style={{background:"#FFF3E0",border:"1px solid "+B.yellow,borderRadius:12,padding:"11px 14px",marginBottom:12,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:20}}>⚠️</span>
        <div style={{flex:1}}><div style={{fontSize:"clamp(11px,1.3vw,13px)",fontWeight:700,color:"#E65100"}}>{uncategorized} lançamentos precisam de categoria</div><div style={{fontSize:10,color:"#795548"}}>Toque para categorizar → resultados imprecisos até lá</div></div>
        <span style={{color:B.yellow}}>›</span>
      </div>)}

      {runway?.zeroDay&&(<div onClick={()=>onGoTo("runway")} style={{background:B.red+"10",border:"1px solid "+B.red,borderRadius:12,padding:"11px 14px",marginBottom:12,cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:20}}>🚨</span>
        <div style={{flex:1}}><div style={{fontSize:"clamp(11px,1.3vw,13px)",fontWeight:700,color:B.red}}>Caixa pode zerar em ~{Math.ceil((new Date(runway.zeroDay)-new Date(runway.lastDate))/86400000)} dias</div><div style={{fontSize:10,color:"#795548"}}>Toque para ver projeção completa</div></div>
        <span style={{color:B.red}}>›</span>
      </div>)}

      <div style={{marginBottom:12}}><PeriodFilter months={months} selected={period} onChange={setPeriod}/></div>

      {/* DRE KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:12}}>
        <KPI label="RECEITA LÍQUIDA" value={fmtC(dre.netRev)} sub={"Bruta: "+fmtC(dre.grossRev)} color={B.green} icon="💰"/>
        <KPI label="LUCRO BRUTO"     value={fmtC(dre.gProfit)} sub={"Margem: "+fmtP(dre.gMargin)} color={B.blue} icon="📈"/>
        <KPI label="EBITDA"          value={fmtC(dre.ebitda)}  sub={"Margem: "+fmtP(dre.ebitdaM)} color={dre.ebitda>=0?B.blue:B.red} icon="🎯" highlight={dre.ebitda>=0}/>
        <KPI label="TOTAL OPEX"      value={fmtC(dre.opex)}    sub={"CMV: "+fmtC(dre.cogs)} color={B.red} icon="📉"/>
      </div>

      {/* CASH BALANCE SUMMARY */}
      <Card title="💰 SALDO DE CAIXA ATUAL" accent={B.green}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:"clamp(20px,2.5vw,28px)",fontWeight:800,color:B.green,fontFamily:"monospace"}}>{fmt(totalCashBalance)}</div>
            <div style={{fontSize:11,color:B.textMut,marginTop:2}}>Soma de todas as contas</div>
          </div>
          {runway&&<div style={{textAlign:"right"}}>
            <div style={{fontSize:11,color:B.textMut}}>Queima diária</div>
            <div style={{fontSize:14,fontWeight:700,color:B.red,fontFamily:"monospace"}}>{fmt(runway.avgDailyBurn)}/dia</div>
            <div style={{fontSize:10,color:B.textMut,marginTop:2}}>Entrada: {fmt(runway.avgDailyIncome)}/dia</div>
          </div>}
        </div>
        {Object.keys(latestBalances||{}).length > 0 && (
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {Object.entries(latestBalances).map(([name,bal])=>(
              <div key={name} style={{background:B.bg,borderRadius:8,padding:"6px 12px",border:"1px solid "+B.border}}>
                <div style={{fontSize:9,color:B.textMut,fontWeight:600}}>{name}</div>
                <div style={{fontSize:12,fontWeight:700,color:B.dark,fontFamily:"monospace"}}>{fmt(bal)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* PROJECTIONS SUMMARY */}
      {(totalProjected>0) && (
        <Card title="📅 PROJEÇÃO DE RECEBIMENTOS" accent={B.blue}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:9,color:B.textMut,fontWeight:600,marginBottom:2}}>TOTAL PROJETADO</div>
              <div style={{fontSize:"clamp(14px,1.8vw,18px)",fontWeight:800,color:B.blue,fontFamily:"monospace"}}>{fmt(totalProjected)}</div>
            </div>
            <div>
              <div style={{fontSize:9,color:B.textMut,fontWeight:600,marginBottom:2}}>RECEITA PONDERADA</div>
              <div style={{fontSize:"clamp(14px,1.8vw,18px)",fontWeight:800,color:B.green,fontFamily:"monospace"}}>{fmt(totalWeighted)}</div>
              <div style={{fontSize:9,color:B.textMut}}>considerando probabilidades</div>
            </div>
          </div>
          {Object.keys(data.pj).length>0&&<MiniBar data={data.pj} c1={B.blue} c2={B.yellow}/>}
        </Card>
      )}

      {/* Margens */}
      <Card title="INDICADORES DE MARGEM">
        <div style={{display:"flex",justifyContent:"space-around",flexWrap:"wrap",gap:8}}>
          <Donut pct={dre.gMargin} label="M. BRUTA" color={B.green} value={fmt(dre.gProfit)}/>
          <Donut pct={Math.max(0,dre.ebitdaM)} label="EBITDA" color={B.blue} value={fmt(dre.ebitda)}/>
          <Donut pct={dre.netRev>0?(dre.cogs/dre.netRev)*100:0} label="CMV" color={B.yellow} value={fmt(dre.cogs)}/>
          <Donut pct={dre.netRev>0?(dre.mkt/dre.netRev)*100:0} label="MKTG" color="#9C27B0" value={fmt(dre.mkt)}/>
        </div>
      </Card>

      {Object.keys(cf).length>0&&(<Card title="FLUXO DE CAIXA MENSAL"><MiniBar data={cf}/><div style={{display:"flex",gap:14,marginTop:8}}><div style={{display:"flex",gap:5,alignItems:"center"}}><div style={{width:10,height:10,background:B.blue,borderRadius:2}}/><span style={{fontSize:10,color:B.textMut}}>Entradas</span></div><div style={{display:"flex",gap:5,alignItems:"center"}}><div style={{width:10,height:10,background:B.red,borderRadius:2}}/><span style={{fontSize:10,color:B.textMut}}>Saídas</span></div></div></Card>)}
    </div>
  );
}

// IMPORT SCREEN
function ImportScreen({txs,projs,onAddTxs,onAddProjs,onClearAll,loading,setLoading,addMsg,transfersPending,setTransfersPending,onResolveTransfers,companies,activeCompany,setActiveCompany}){
  const [proc,setProc]=useState(null);

  const handleMP = async file => {
    setProc("mp"); setLoading(p=>({...p,mp:true}));
    try {
      const buf = await file.arrayBuffer();
      const {transactions, balanceByDate, needsTransferReview} = parseMPXLSX(new Uint8Array(buf));
      if(!transactions.length && !needsTransferReview.length) throw new Error("Nenhuma transação encontrada.");
      const {added,dupes,periods} = onAddTxs(transactions,"Mercado Pago",balanceByDate,activeCompany);
      const withType = transactions.filter(t=>t.transactionType).length;
      let msg = "✅ **Extrato Mercado Pago importado!**\n\n📊 **"+added+" transações** adicionadas ("+dupes+" duplicadas ignoradas)\n📅 Períodos: "+periods.join(", ")+"\n🏷️ "+withType+" com TRANSACTION_TYPE\n💰 "+Object.keys(balanceByDate).length+" saldos diários capturados";
      if (needsTransferReview.length) {
        msg += "\n\n⚠️ **"+needsTransferReview.length+" transferências de saída** precisam ser classificadas — responda as perguntas abaixo!";
        setTransfersPending(needsTransferReview.map(t=>({...t, companyId: activeCompany||"default"})));
      }
      addMsg("assistant", msg);
    } catch(e) { addMsg("assistant","⚠️ Erro MP: "+e.message); }
    finally { setLoading(p=>({...p,mp:false})); setProc(null); }
  };

  const handleOmie = async file => {
    setProc("omie"); setLoading(p=>({...p,omie:true}));
    try {
      addMsg("assistant","📄 Processando extrato Omie Cash...");
      const buf = await file.arrayBuffer();
      const {transactions, balanceByDate} = await parseOmiePDF(buf);
      const {added,dupes,periods,conflict} = onAddTxs(transactions,"Omie Cash",balanceByDate,activeCompany);
      let msg = "✅ **Omie Cash processado!**\n\n📊 **"+added+" transações** adicionadas\n📅 Períodos: "+periods.join(", ")+"\n💰 "+Object.keys(balanceByDate).length+" saldos diários";
      if (conflict.length) msg += "\n⚠️ Períodos já existentes: "+conflict.join(", ");
      addMsg("assistant",msg);
    } catch(e) { addMsg("assistant","⚠️ Erro Omie: "+e.message); }
    finally { setLoading(p=>({...p,omie:false})); setProc(null); }
  };

  const handleB2B = async file => {
    setProc("b2b"); setLoading(p=>({...p,b2b:true}));
    try {
      const buf = await file.arrayBuffer();
      const result = parseB2BXLSX(new Uint8Array(buf));
      if (!result.length) throw new Error("Nenhum recebimento encontrado. Verifique o formato.");
      const {added,updated} = onAddProjs(result, activeCompany);
      addMsg("assistant","✅ **"+result.length+" recebimentos B2B** importados!\n\n📅 **"+added+" novos** adicionados, **"+updated+" atualizados**.\n\nTotal: R$ "+result.reduce((s,p)=>s+p.expectedRevenue,0).toLocaleString("pt-BR",{minimumFractionDigits:2})+"\n\nVeja em **Proj.**");
    } catch(e) {
      addMsg("assistant","❌ Erro ao importar B2B: "+e.message);
    } finally { setLoading(p=>({...p,b2b:false})); setProc(null); }
  };

  const handleImage = async file => {
    setProc("img"); setLoading(p=>({...p,img:true}));
    try {
      addMsg("assistant","🔍 Analisando imagem de recebimentos futuros com IA...\n\nAguarde alguns segundos enquanto processo a imagem.");
      // Convert to base64
      const b64 = await toB64(file);
      // Detect media type properly
      const mt = file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";
      const result = await parseImageAPI(b64, mt);
      const {added,updated} = onAddProjs(result, activeCompany);
      addMsg("assistant","✅ **"+result.length+" recebimentos futuros** extraídos da imagem!\n\n📅 **"+added+" novos** adicionados, **"+updated+" atualizados**.\n\nVeja os detalhes na aba **Projeções** e no gráfico de **Caixa**.");
    } catch(e) {
      addMsg("assistant","⚠️ **Erro ao processar imagem:**\n"+e.message+"\n\n💡 **Dica:** Certifique-se que a imagem mostra claramente os recebimentos futuros do Mercado Pago.");
    }
    finally { setLoading(p=>({...p,img:false})); setProc(null); }
  };

  const cId = activeCompany||"default";
  const compTxs = txs.filter(t=>(t.companyId||"default")===cId);
  const months = [...new Set(compTxs.map(t=>ym(t.competenceDate||t.date)).filter(Boolean))].sort();
  const sources = [...new Set(compTxs.map(t=>t.source))];

  const activeComp = companies.find(c=>c.id===activeCompany);

  const cards = [
    {key:"mp",   label:"Extrato Mercado Pago", sub:"XLSX · TRANSACTION_TYPE + PARTIAL_BALANCE", color:B.green,  icon:"💳", accept:".xlsx,.xls", fn:handleMP,   count:compTxs.filter(t=>t.source==="Mercado Pago").length},
    {key:"omie", label:"Extrato Omie Cash",    sub:"PDF · Categoria + Saldo diário",            color:B.blue,   icon:"🏦", accept:".pdf",       fn:handleOmie, count:compTxs.filter(t=>t.source==="Omie Cash").length},
    {key:"img",  label:"Recebimentos Futuros", sub:"Print MP · usado na projeção de caixa",     color:B.yellow, icon:"📸", accept:"image/*",    fn:handleImage,count:projs.filter(p=>(p.companyId||"default")===cId).length},
    {key:"b2b", label:"A Receber B2B (Omie)", sub:"XLSX · Contas a Receber Cliente",      color:"#9C27B0", icon:"🏢", accept:".xlsx,.xls", fn:handleB2B, count:projs.filter(p=>(p.companyId||"default")===cId&&p.source==="Omie B2B").length},
  ];

  return(
    <div style={{padding:"clamp(12px,2vw,20px)"}}>
      {transfersPending&&transfersPending.length>0&&(
        <TransferReviewModal transfers={transfersPending} onDone={decisions=>{onResolveTransfers(decisions);setTransfersPending(null);}}/>
      )}
      {compTxs.length>0&&(
        <div style={{background:B.card,border:"1px solid "+B.border,borderRadius:12,padding:"12px 14px",marginBottom:14,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{textAlign:"center"}}><div style={{fontSize:"clamp(18px,2.5vw,24px)",fontWeight:800,color:B.green}}>{compTxs.length}</div><div style={{fontSize:9,color:B.textMut,fontWeight:600}}>TRANSAÇÕES</div></div>
            <div style={{textAlign:"center"}}><div style={{fontSize:"clamp(18px,2.5vw,24px)",fontWeight:800,color:B.blue}}>{compTxs.length>0?projs.filter(p=>(p.companyId||"default")===cId).length:0}</div><div style={{fontSize:9,color:B.textMut,fontWeight:600}}>PROJEÇÕES</div></div>
            <div style={{flex:1,minWidth:120}}>
              <div style={{fontSize:9,color:B.textMut,fontWeight:600,marginBottom:4}}>FONTES</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{sources.map(s=><span key={s} style={{fontSize:9,background:B.green+"18",color:B.green,padding:"2px 8px",borderRadius:10,border:"1px solid "+B.green+"44",fontWeight:600}}>{s}</span>)}</div>
            </div>
          </div>
          {months.length>0&&<div style={{marginTop:8,fontSize:10,color:B.textMut}}><strong>Períodos:</strong> {months.map(m=>m.split("-").reverse().join("/")).join(" · ")}</div>}
        </div>
      )}
      {/* Company selector */}
      {companies.length > 0 && (
        <div style={{background:B.card,border:"1px solid "+B.border,borderRadius:12,padding:"12px 14px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:10,color:B.textMut,fontWeight:700,letterSpacing:"0.08em",marginBottom:8}}>🏢 IMPORTAR PARA A EMPRESA</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {companies.map(c=>(
              <button key={c.id} onClick={()=>setActiveCompany(c.id)}
                style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid "+(activeCompany===c.id?B.blue:B.border2),background:activeCompany===c.id?B.blue+"18":B.card,color:activeCompany===c.id?B.blue:B.textSub,fontSize:12,fontWeight:activeCompany===c.id?700:400,cursor:"pointer"}}>
                {activeCompany===c.id?"✓ ":""}{c.name}
              </button>
            ))}
          </div>
          {activeComp && <div style={{fontSize:10,color:B.textMut,marginTop:6}}>Os arquivos serão vinculados a <strong style={{color:B.blue}}>{activeComp.name}</strong></div>}
          {!activeComp && <div style={{fontSize:10,color:B.red,marginTop:6}}>⚠️ Selecione uma empresa acima para vincular os dados</div>}
        </div>
      )}
      {companies.length === 0 && (
        <div style={{background:"#FFF8E1",border:"1px solid "+B.yellow+"88",borderRadius:12,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#795548"}}>
          💡 Crie uma empresa em <strong>Configurações</strong> para organizar os dados por cliente.
        </div>
      )}

      {cards.map(({key,label,sub,color,icon,accept,fn,count})=>(
        <label key={key} style={{display:"block",marginBottom:10,cursor:proc?"wait":"pointer"}}>
          <input type="file" accept={accept} style={{display:"none"}} disabled={!!proc} onChange={async e=>{if(e.target.files[0]){await fn(e.target.files[0]);e.target.value="";}}}/>
          <div style={{background:B.card,border:"1.5px solid "+(loading[key]?B.yellow:count>0?color:B.border),borderRadius:14,padding:"clamp(12px,1.8vw,16px)",display:"flex",alignItems:"center",gap:14,transition:"all 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
            <div style={{width:44,height:44,borderRadius:11,background:color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{loading[key]?"⏳":icon}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"clamp(12px,1.5vw,14px)",fontWeight:700,color:count>0?color:B.text,marginBottom:2}}>{label}</div>
              <div style={{fontSize:"clamp(10px,1.1vw,11px)",color:B.textMut}}>{sub}</div>
              {count>0&&!loading[key]&&<div style={{fontSize:10,color,marginTop:3,fontWeight:600}}>✓ {count} registros · toque para adicionar mais</div>}
              {loading[key]&&<div style={{fontSize:10,color:B.yellow,marginTop:3,fontWeight:600}}>Processando...</div>}
            </div>
            <div style={{fontSize:22,color:count>0?color:B.border2}}>{count>0?"✅":"↑"}</div>
          </div>
        </label>
      ))}
      {compTxs.length>0&&(
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={onClearAll} style={{flex:1,background:"transparent",border:"1px solid "+B.border2,borderRadius:10,padding:"10px",color:B.textMut,fontSize:12,cursor:"pointer"}}>
            🗑️ Limpar dados de <strong style={{color:activeComp?B.red:B.textMut}}>{activeComp?activeComp.name:"empresa selecionada"}</strong>
          </button>
        </div>
      )}

      {/* How to save & share info */}
      <div style={{background:"#E3F2FD",border:"1px solid "+B.blue+"44",borderRadius:12,padding:"14px 16px",marginTop:12}}>
        <div style={{fontSize:12,fontWeight:700,color:B.blue,marginBottom:10}}>💾 Como salvar e compartilhar</div>
        <div style={{fontSize:11,color:B.dark,lineHeight:1.8}}>
          <div>✅ <strong>Os dados são salvos automaticamente</strong> neste navegador/dispositivo via armazenamento local.</div>
          <div style={{marginTop:6}}>📤 <strong>Para compartilhar com o cliente:</strong></div>
          <div style={{marginLeft:16,marginTop:4,padding:"8px 12px",background:B.card,borderRadius:8,border:"1px solid "+B.border}}>
            <div style={{fontSize:10,color:B.textMut,marginBottom:4}}>URL DO PORTAL (mesmo link deste app)</div>
            <div style={{fontFamily:"monospace",fontSize:11,color:B.dark,wordBreak:"break-all"}}>Acesse: <strong>claude.ai</strong> → abra este artifact → o cliente clica em <strong>"Portal do Cliente"</strong> → digita a senha da empresa</div>
          </div>
          <div style={{marginTop:8}}>⚠️ <strong>Atenção:</strong> Os dados ficam neste dispositivo. Para uso em produção real, hospede o app em um servidor (ex: Vercel, Netlify) e o link será fixo.</div>
        </div>
      </div>
    </div>
  );
}

// CATEGORIZE SCREEN
function CategorizeScreen({txs,setTxs,brief,addMsg,companyId,company}){
  const [mode,setMode]=useState("review");
  const [sugLoading,setSugLoading]=useState({});
  const [aiSugs,setAiSugs]=useState({});
  const [editing,setEditing]=useState(null);
  const [bulkLoading,setBulkLoading]=useState(false);

  const cId = companyId||"default";
  const companyTxList = txs.filter(t=>(t.companyId||"default")===cId);
  const toReview = companyTxList.filter(t=>t.needsReview||t.category==="Outros"||t.category==="Aguardando Classificação");
  const list = mode==="review" ? toReview : companyTxList;

  const companyCats = getCompanyCategories(company);
  const companyCatNames = companyCats.map(c=>c.name);

  const updateCat = (id,cat) => {
    setTxs(prev=>prev.map(t=>t.id===id?{...t,category:cat,needsReview:false}:t));
    setEditing(null);
  };

  const getSug = async tx => {
    setSugLoading(p=>({...p,[tx.id]:true}));
    try {
      const sysPrompt = "Classifique este lancamento em UMA categoria. "+(brief?"Contexto: "+brief+". ":"")+"Categorias disponíveis: "+companyCatNames.join(", ")+". Responda SOMENTE JSON sem markdown com os campos category, reason e confidence.";
      const userContent = "Classifique: \""+tx.description+"\""+(tx.transactionType?" | TRANSACTION_TYPE: "+tx.transactionType:"")+(tx.omieCategory&&tx.omieCategory!=="-"?" | Omie: "+tx.omieCategory:"");
      const r = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:150,system:sysPrompt,messages:[{role:"user",content:userContent}]})});
      const raw=await r.text(); let d; try{d=JSON.parse(raw);}catch{throw new Error("API invalida");}
      if(d.error) throw new Error(d.error.message);
      const text=(d.content||[]).find(b=>b.type==="text")?.text||"{}";
      const result = JSON.parse(text.replace(/```json|```/g,"").trim());
      setAiSugs(p=>({...p,[tx.id]:result}));
    } catch(e) { addMsg("assistant","⚠️ "+e.message); }
    setSugLoading(p=>({...p,[tx.id]:false}));
  };

  const bulkApply = async () => {
    setBulkLoading(true);
    try {
      const pending = toReview.slice(0,40);
      const items = pending.map((t,i)=>i+": \""+t.description+"\" | tipo:"+((t.transactionType||t.omieCategory)||"-")+" | "+t.type).join("\n");
      const bulkSys = "Classifique lançamentos financeiros. "+(brief?"Empresa: "+brief+". ":"")+"Categorias disponíveis: "+companyCatNames.join(", ")+". Responda SOMENTE JSON sem markdown com campo results sendo array de objetos com index, category e confidence.";
      const r = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:2000,system:bulkSys,messages:[{role:"user",content:"Classifique:\n"+items}]})});
      const raw=await r.text(); let d; try{d=JSON.parse(raw);}catch{throw new Error("API invalida");}
      if(d.error) throw new Error(d.error.message);
      const text=(d.content||[]).find(b=>b.type==="text")?.text||"{}";
      const parsed=JSON.parse(text.replace(/```json|```/g,"").trim());
      const results=parsed.results||[];
      let applied = 0;
      setTxs(prev=>prev.map(t=>{
        const p=pending.findIndex(x=>x.id===t.id); if(p===-1) return t;
        const s=results.find(r=>r.index===p);
        if(s&&s.confidence>=0.75&&companyCatNames.includes(s.category)){applied++;return{...t,category:s.category,needsReview:false};}
        return t;
      }));
      addMsg("assistant","✅ **"+applied+" lançamentos** categorizados (confiança ≥75%).");
    } catch(e) { addMsg("assistant","⚠️ Erro: "+e.message); }
    setBulkLoading(false);
  };

  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      <div style={{display:"flex",gap:6,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        <Pill label={"Revisar ("+toReview.length+")"} selected={mode==="review"} color={B.red} onClick={()=>setMode("review")}/>
        <Pill label="Todos" selected={mode==="all"} color={B.blue} onClick={()=>setMode("all")}/>
        {toReview.length>0&&<button onClick={bulkApply} disabled={bulkLoading} style={{marginLeft:"auto",background:bulkLoading?B.border:B.blue,border:"none",borderRadius:20,padding:"6px 14px",color:"white",fontSize:11,fontWeight:700,cursor:bulkLoading?"default":"pointer"}}>{bulkLoading?"⏳ IA...":"✨ Categorizar tudo com IA"}</button>}
      </div>
      {list.length===0&&<div style={{padding:"40px 20px",textAlign:"center",color:B.textMut,fontSize:13}}>{mode==="review"?"✅ Todos categorizados!":"Nenhuma transação."}</div>}
      {list.map(t=>{
        const sug=aiSugs[t.id]; const cc=getCatColor(t.category,company);
        const isAwait = t.category==="Aguardando Classificação";
        return(
          <div key={t.id} style={{background:B.card,border:"1px solid "+(isAwait?"#FF6F00":t.needsReview?B.yellow+"88":B.border),borderRadius:12,padding:"clamp(10px,1.5vw,14px)",marginBottom:8,boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div style={{flex:1,minWidth:0,paddingRight:8}}>
                <div style={{fontSize:"clamp(11px,1.3vw,13px)",color:B.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.description}</div>
                <div style={{fontSize:9,color:B.textMut,marginTop:2,display:"flex",gap:6,flexWrap:"wrap"}}>
                  <span>{t.date}</span><span>{t.source}</span>
                  {t.transactionType&&<span style={{color:B.blue}}>MP: {t.transactionType}</span>}
                  {t.omieCategory&&t.omieCategory!=="-"&&<span style={{color:"#9C27B0"}}>Omie: {t.omieCategory}</span>}
                  {t.reference&&<span>Ref: {t.reference}</span>}
                </div>
              </div>
              <div style={{fontFamily:"monospace",fontSize:"clamp(12px,1.4vw,14px)",fontWeight:700,color:t.type==="credit"?B.green:B.red,flexShrink:0}}>{t.type==="credit"?"+":"-"}{fmt(t.type==="credit"?(t.grossAmount||t.amount):t.amount)}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:9,background:cc+"18",color:cc,border:"1px solid "+cc+"44",borderRadius:8,padding:"2px 8px",fontWeight:700}}>{isAwait?"⏳ ":""}{t.category}</span>
              <button onClick={()=>getSug(t)} disabled={sugLoading[t.id]} style={{fontSize:9,background:B.blue+"12",border:"1px solid "+B.blue+"44",borderRadius:8,padding:"2px 8px",color:B.blue,cursor:"pointer",fontWeight:600}}>{sugLoading[t.id]?"⏳":"✨ IA"}</button>
              <button onClick={()=>setEditing(editing===t.id?null:t.id)} style={{fontSize:9,background:B.border,border:"none",borderRadius:8,padding:"2px 8px",color:B.textSub,cursor:"pointer",fontWeight:600}}>✏️ Editar</button>
            </div>
            {sug&&<div style={{marginTop:8,background:B.blue+"10",border:"1px solid "+B.blue+"44",borderRadius:8,padding:"7px 10px",fontSize:11}}>
              <strong style={{color:B.blue}}>✨ IA sugere: {sug.category}</strong>
              {sug.reason&&<div style={{color:B.textSub,marginTop:2}}>{sug.reason}</div>}
              {sug.category&&<button onClick={()=>{updateCat(t.id,sug.category);setAiSugs(p=>{const n={...p};delete n[t.id];return n;});}} style={{marginTop:6,background:B.blue,border:"none",borderRadius:6,padding:"4px 10px",color:"white",fontSize:10,cursor:"pointer",fontWeight:700}}>Aplicar</button>}
            </div>}
            {editing===t.id&&(
              <div style={{marginTop:8,display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:4}}>
                {companyCats.map(cat=>{
                  const cc2=cat.color||B.textMut;
                  const subs=cat.subcategories||[];
                  return(
                    <div key={cat.name}>
                      <button onClick={()=>updateCat(t.id,cat.name)} style={{width:"100%",padding:"7px 8px",borderRadius:8,border:"1.5px solid "+(t.category===cat.name?cc2:B.border),background:t.category===cat.name?cc2+"18":"transparent",color:t.category===cat.name?cc2:B.textSub,fontSize:10,fontWeight:t.category===cat.name?700:400,cursor:"pointer",textAlign:"left"}}>
                        {cat.name}
                      </button>
                      {subs.length>0&&subs.map(sub=>(
                        <button key={sub.id} onClick={()=>updateCat(t.id,cat.name+"/"+sub.name)} style={{width:"100%",marginTop:2,padding:"4px 8px 4px 16px",borderRadius:6,border:"1px solid "+(t.category===cat.name+"/"+sub.name?cc2:B.border+"88"),background:t.category===cat.name+"/"+sub.name?cc2+"12":"transparent",color:t.category===cat.name+"/"+sub.name?cc2:B.textMut,fontSize:9,cursor:"pointer",textAlign:"left"}}>
                          └ {sub.name}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// DRE SCREEN
function DREScreen({data,months,period,setPeriod}){
  if(!data||!data.allTxs.length) return <div style={{padding:"60px 16px",textAlign:"center",color:B.textMut,fontSize:14}}><div style={{fontSize:42,marginBottom:12}}>📑</div>Importe extratos para ver a DRE</div>;
  const {dre,catTxMap,subCatMap} = data;
  const rows = [
    {label:"Receita Bruta de Vendas",v:dre.grossRev,cat:"Receita de Vendas"},
    {label:"(—) Devoluções",v:-dre.ret,neg:true,cat:"Devoluções"},
    {label:"= RECEITA LÍQUIDA",v:dre.netRev,total:true},
    {label:"(—) CMV "+(dre.cmvManual>0?"✓ ERP":"(extrato)"),v:-dre.cogs,neg:true,cat:"Custo de Mercadoria",sub:dre.cmvManual>0?"ERP: "+fmt(dre.cmvManual)+" · Extrato: "+fmt(dre.cogsAuto):""},
    {label:"= LUCRO BRUTO",v:dre.gProfit,total:true,sub:"Margem Bruta: "+fmtP(dre.gMargin)},
    {label:"Despesas com Pessoal",v:-dre.pers,neg:true,cat:"Despesas com Pessoal"},
    {label:"Marketing e Publicidade",v:-dre.mkt,neg:true,cat:"Marketing"},
    {label:"Frete",v:-dre.frete,neg:true,cat:"Frete"},
    {label:"Despesas Fixas",v:-dre.fix,neg:true,cat:"Despesas Fixas"},
    {label:"Despesas Operacionais",v:-dre.ops,neg:true,cat:"Despesas Operacionais"},
    {label:"Tarifas Bancárias",v:-dre.fees,neg:true,cat:"Tarifas e Taxas"},
    {label:"= TOTAL OPEX",v:-dre.opex,total:true,neg:true},
    {label:"= EBITDA",v:dre.ebitda,total:true,highlight:true,sub:"Margem EBITDA: "+fmtP(dre.ebitdaM)},
  ];
  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      <div style={{marginBottom:12}}><PeriodFilter months={months} selected={period} onChange={setPeriod}/></div>
      {period&&<div style={{fontSize:11,color:B.blue,fontWeight:600,marginBottom:10}}>📅 Período: {period.split("-").reverse().join("/")} {dre.cmvManual>0?"· CMV ERP: "+fmt(dre.cmvManual):""}</div>}
      <Card noPad>
        <div style={{padding:"clamp(12px,1.8vw,16px)",display:"flex",alignItems:"center",gap:8,borderBottom:"1px solid "+B.border}}>
          <div style={{width:4,height:20,borderRadius:2,background:"linear-gradient("+B.red+","+B.blue+")"}}/>
          <span style={{fontSize:"clamp(12px,1.6vw,15px)",fontWeight:800,color:B.dark}}>Demonstrativo de Resultado — DRE</span>
        </div>
        <div style={{fontSize:10,color:B.textMut,padding:"6px 16px",background:"#FAFAFA",borderBottom:"1px solid "+B.border}}>
          💡 Toque nas linhas para ver sub-categorias → depois os lançamentos
        </div>
        {rows.map(({label,v,total,neg,highlight,sub,cat},i)=>(
          <DRERow key={i} label={label} value={v} total={total} neg={neg} highlight={highlight} sub={sub} catKey={cat} subCatMap={subCatMap} catTxMap={catTxMap}/>
        ))}
      </Card>
    </div>
  );
}

// CASHFLOW SCREEN
function CashflowScreen({data,months,period,setPeriod,setTxs,company}){
  const [filter,setFilter]=useState("todos");
  const [editingId,setEditingId]=useState(null);

  const updateCat=(id,cat)=>{setTxs(prev=>prev.map(t=>t.id===id?{...t,category:cat,needsReview:false}:t));setEditingId(null);};
  const companyCats = getCompanyCategories(company);

  if(!data||!data.allTxs.length) return <div style={{padding:"60px 16px",textAlign:"center",color:B.textMut,fontSize:14}}><div style={{fontSize:42,marginBottom:12}}>💰</div>Importe extratos</div>;
  // Use allTxs (all periods for this company), then apply period+source filter
  const baseTxs = data.allTxs;
  const filteredBase = period ? baseTxs.filter(t=>ym(t.date)===period) : baseTxs;
  const list = filter==="todos"?filteredBase:filter==="entradas"?filteredBase.filter(t=>t.type==="credit"):filteredBase.filter(t=>t.type==="debit");
  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      <div style={{marginBottom:10}}><PeriodFilter months={months} selected={period} onChange={setPeriod}/></div>
      {Object.keys(data.cf).length>0&&<Card title="FLUXO MENSAL"><MiniBar data={data.cf}/></Card>}
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {[["todos","Todos"],["entradas","Entradas"],["saídas","Saídas"]].map(([f,l])=><Pill key={f} label={l} selected={filter===f} color={B.blue} onClick={()=>setFilter(f)}/>)}
      </div>
      <Card noPad>
        <div style={{paddingTop:4}}>
          {list.length===0&&<div style={{padding:20,textAlign:"center",color:B.textMut,fontSize:12}}>Nenhuma transação</div>}
          {list.map((t,i)=>{
            const cc=getCatColor(t.category,company);
            const isEditing=editingId===t.id;
            return(
            <div key={t.id||i} style={{borderBottom:i<list.length-1?"1px solid "+B.border:"none"}}>
              {/* Main row */}
              <div style={{padding:"10px clamp(10px,1.5vw,14px)",display:"flex",alignItems:"flex-start",gap:10,background:isEditing?"#F0F7FF":"transparent"}}>
                <div style={{width:32,height:32,borderRadius:8,flexShrink:0,background:t.type==="credit"?B.green+"18":B.red+"18",border:"1px solid "+(t.type==="credit"?B.green+"44":B.red+"44"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:t.type==="credit"?B.green:B.red,fontWeight:700}}>
                  {t.type==="credit"?"↑":"↓"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"clamp(11px,1.3vw,13px)",color:B.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.description}</div>
                  <div style={{display:"flex",gap:5,marginTop:3,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={{fontSize:9,color:B.textMut}}>{t.date}</span>
                    <span style={{fontSize:9,background:cc+"18",color:cc,border:"1px solid "+cc+"44",borderRadius:6,padding:"1px 6px",fontWeight:600,cursor:"pointer"}} onClick={()=>setEditingId(isEditing?null:t.id)}>
                      {t.category} ✏️
                    </span>
                    {t.transactionType&&<span style={{fontSize:9,color:B.blue,background:B.blue+"10",borderRadius:6,padding:"0 5px"}}>{t.transactionType}</span>}
                    {t.omieCategory&&t.omieCategory!=="-"&&<span style={{fontSize:9,color:"#9C27B0",background:"#9C27B018",borderRadius:6,padding:"0 5px"}}>{t.omieCategory}</span>}
                    <span style={{fontSize:9,color:B.textMut}}>{t.source}</span>
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:"monospace",fontSize:"clamp(11px,1.3vw,13px)",fontWeight:700,color:t.type==="credit"?B.green:B.red}}>{t.type==="credit"?"+":"-"}{fmt(t.type==="credit"?(t.grossAmount||t.amount):t.amount)}</div>
                  {t.balance>0&&<div style={{fontSize:9,color:B.textMut,fontFamily:"monospace"}}>Saldo: {fmtC(t.balance)}</div>}
                </div>
              </div>
              {/* Inline category editor */}
              {isEditing&&(
                <div style={{background:"#F0F7FF",borderTop:"1px solid "+B.blue+"22",padding:"10px clamp(10px,1.5vw,14px)"}}>
                  <div style={{fontSize:10,color:B.blue,fontWeight:700,marginBottom:8}}>✏️ Alterar categoria de: <em style={{color:B.dark}}>{t.description.slice(0,40)}</em></div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:5}}>
                    {companyCats.map(cat=>{
                      const cc2=cat.color||B.textMut;
                      const subs = cat.subcategories||[];
                      return(
                        <div key={cat.name}>
                          <button onClick={()=>updateCat(t.id,cat.name)} style={{width:"100%",padding:"6px 8px",borderRadius:8,border:"1.5px solid "+(t.category===cat.name?cc2:B.border2),background:t.category===cat.name?cc2+"18":"white",color:t.category===cat.name?cc2:B.textSub,fontSize:10,fontWeight:t.category===cat.name?700:400,cursor:"pointer",textAlign:"left",transition:"all 0.1s"}}>
                            {cat.name}
                          </button>
                          {subs.length>0&&subs.map(sub=>(
                            <button key={sub.id} onClick={()=>updateCat(t.id,cat.name+"/"+sub.name)} style={{width:"100%",marginTop:2,padding:"4px 8px 4px 16px",borderRadius:6,border:"1px solid "+(t.category===cat.name+"/"+sub.name?cc2:B.border+"88"),background:t.category===cat.name+"/"+sub.name?cc2+"12":"transparent",color:t.category===cat.name+"/"+sub.name?cc2:B.textMut,fontSize:9,cursor:"pointer",textAlign:"left"}}>
                              └ {sub.name}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={()=>setEditingId(null)} style={{marginTop:8,background:"transparent",border:"1px solid "+B.border2,borderRadius:8,padding:"4px 12px",color:B.textMut,fontSize:10,cursor:"pointer"}}>Cancelar</button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// RUNWAY SCREEN
function RunwayScreen({runway}){
  if (!runway) return(
    <div style={{padding:"60px 20px",textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:12}}>💧</div>
      <div style={{color:B.textSub,fontSize:14,lineHeight:1.6}}>Importe extratos com saldos para ver a projeção de caixa.<br/><span style={{fontSize:12,color:B.textMut}}>MP: coluna BALANCE · Omie: coluna Saldo</span></div>
    </div>
  );
  const {historical,forecast,lastDate,lastBalance,avgDailyBurn,avgDailyIncome,avgDailyNet,zeroDay} = runway;
  const daysLeft = zeroDay ? Math.ceil((new Date(zeroDay)-new Date(lastDate))/(1000*60*60*24)) : null;
  const urgency = daysLeft!==null ? (daysLeft<=7?"critical":daysLeft<=30?"warning":"ok") : "ok";
  const alertC = urgency==="critical"?B.red:urgency==="warning"?B.yellow:B.green;

  const all = [...historical,...forecast].slice(-60);
  const maxBal = Math.max(...all.map(d=>d.balance),1);
  const W=400,H=110,pad=10;
  const splitIdx = historical.length-1;
  const toXY = (d,i) => { const x=pad+i*((W-pad*2)/(all.length-1||1)), y=H-pad-(d.balance/maxBal)*(H-pad*2); return [x,y]; };
  const histPts = all.slice(0,splitIdx+1).map((d,i)=>toXY(d,i).join(",")).join(" ");
  const projPts = all.slice(splitIdx).map((d,i)=>toXY(d,splitIdx+i).join(",")).join(" ");
  const [tipRw,setTipRw]=useState(null);
  const allPts = all.map((d,i)=>({...d,...{xy:toXY(d,i)}}));

  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      <div style={{background:alertC+"12",border:"1.5px solid "+alertC,borderRadius:14,padding:"14px 16px",marginBottom:14}}>
        <div style={{fontSize:"clamp(13px,1.8vw,16px)",fontWeight:800,color:alertC,marginBottom:4}}>
          {zeroDay ? "⚠️ Caixa zera em ~"+daysLeft+" dias ("+zeroDay+")" : "✅ Caixa positivo nos próximos 90 dias"}
        </div>
        <div style={{fontSize:"clamp(11px,1.3vw,13px)",color:urgency==="ok"?B.textSub:"#795548"}}>
          Saldo atual: <strong style={{color:alertC}}>{fmt(lastBalance)}</strong> · Queima média diária: <strong>{fmt(avgDailyBurn)}</strong>
        </div>
        <div style={{fontSize:11,color:B.textMut,marginTop:4}}>
          📊 Projeção baseada em despesas operacionais fixas dos últimos 30 dias + recebimentos futuros cadastrados
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:14}}>
        <KPI label="SALDO ATUAL" value={fmtC(lastBalance)} color={B.blue} icon="💰"/>
        <KPI label="OPEX/DIA MÉDIO" value={fmtC(avgDailyBurn)} color={B.red} icon="↓" sub="despesas fixas"/>
        <KPI label="RECEITA/DIA MÉDIO" value={fmtC(avgDailyIncome)} color={B.green} icon="↑"/>
        <KPI label="SALDO LÍQUIDO/DIA" value={fmtC(avgDailyNet)} color={avgDailyNet>=0?B.green:B.red} icon="⚖️"/>
      </div>
      <Card title="PROJEÇÃO DE SALDO (histórico + futuro)">
        <div style={{position:"relative"}}>
          {tipRw&&<div style={{position:"absolute",top:0,left:tipRw.pct,transform:"translateX(-50%)",background:B.dark,color:"white",padding:"4px 9px",borderRadius:6,fontSize:10,whiteSpace:"nowrap",zIndex:10,pointerEvents:"none",boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
            <div style={{fontWeight:700,color:tipRw.isProj?B.green:B.blue}}>{tipRw.date}</div>
            <div>{fmt(tipRw.balance)}</div>
            <div style={{fontSize:8,color:"rgba(255,255,255,0.6)"}}>{tipRw.isProj?"Projeção":"Realizado"}</div>
          </div>}
          <svg viewBox={"0 0 "+W+" "+(H+12)} style={{width:"100%",overflow:"visible"}} onMouseLeave={()=>setTipRw(null)}>
            {histPts&&<polyline points={histPts} fill="none" stroke={B.blue} strokeWidth={2} strokeLinejoin="round"/>}
            {projPts&&<polyline points={projPts} fill="none" stroke={B.green} strokeWidth={2} strokeDasharray="4,3" strokeLinejoin="round"/>}
            {allPts.map((d,i)=>{
              const [x,y]=d.xy; const isProj=i>splitIdx;
              return <circle key={i} cx={x} cy={y} r={5} fill="transparent" stroke="transparent"
                onMouseEnter={()=>setTipRw({date:d.date,balance:d.balance,isProj,pct:((x/W)*100)+"%"})}
                style={{cursor:"crosshair"}}/>;
            })}
            <line x1={pad} y1={H-pad} x2={W-pad} y2={H-pad} stroke={B.border2} strokeWidth={1}/>
            <text x={pad} y={H+10} fill={B.textMut} fontSize={7}>Histórico</text>
            <text x={W-38} y={H+10} fill={B.green} fontSize={7}>Projeção</text>
          </svg>
        </div>
        <div style={{display:"flex",gap:16,marginTop:6}}>
          <div style={{display:"flex",gap:5,alignItems:"center"}}><div style={{width:20,height:2,background:B.blue}}/><span style={{fontSize:10,color:B.textMut}}>Realizado</span></div>
          <div style={{display:"flex",gap:5,alignItems:"center"}}><div style={{width:20,height:2,background:B.green,borderTop:"2px dashed "+B.green}}/><span style={{fontSize:10,color:B.textMut}}>Projetado (OPEX+recebimentos)</span></div>
        </div>
      </Card>
            <Card title="SALDO DIÁRIO" noPad>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"clamp(10px,1.2vw,12px)"}}>
            <thead><tr style={{background:B.bg}}>
              {["Data","Saldo","Tipo"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:9,color:B.textMut,fontWeight:700,letterSpacing:"0.08em",borderBottom:"1px solid "+B.border}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {[...historical.slice(-7),...forecast.slice(0,10)].map((d,i)=>(
                <tr key={i} style={{background:d.isProjected?"#F0F8FF":"white"}}>
                  <td style={{padding:"8px 12px",borderBottom:"1px solid "+B.border,fontFamily:"monospace",color:B.text}}>{d.date}</td>
                  <td style={{padding:"8px 12px",borderBottom:"1px solid "+B.border,fontFamily:"monospace",fontWeight:600,color:d.balance<1000?B.red:d.balance<5000?B.yellow:B.green}}>{fmt(d.balance)}</td>
                  <td style={{padding:"8px 12px",borderBottom:"1px solid "+B.border}}><span style={{fontSize:9,background:d.isProjected?B.green+"18":B.blue+"18",color:d.isProjected?B.green:B.blue,borderRadius:8,padding:"1px 7px",fontWeight:700}}>{d.isProjected?"PROJEÇÃO":"REALIZADO"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// PROJECTIONS SCREEN
function ProjectionsScreen({data}){
  if(!data||!data.projs.length) return <div style={{padding:"60px 16px",textAlign:"center",color:B.textMut,fontSize:14}}><div style={{fontSize:42,marginBottom:12}}>📈</div>Importe a imagem de recebimentos futuros</div>;
  const {pj,projs} = data;
  const totalP=Object.values(pj).reduce((a,b)=>a+b.projected,0);
  const totalW=Object.values(pj).reduce((a,b)=>a+b.weighted,0);
  return(
    <div style={{padding:"0 clamp(12px,2vw,20px) 24px"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:12}}>
        <KPI label="TOTAL PROJETADO" value={fmtC(totalP)} color={B.blue} icon="📊"/>
        <KPI label="RECEITA PONDERADA" value={fmtC(totalW)} color={B.green} icon="✅"/>
      </div>
      {Object.keys(pj).length>0&&<Card title="PROJETADO vs PONDERADO"><MiniBar data={pj} c1={B.blue} c2={B.yellow}/></Card>}
      <Card noPad>
        <div style={{padding:"11px 14px 6px",fontSize:11,fontWeight:700,color:B.textSub,borderBottom:"1px solid "+B.border}}>RECEBIMENTOS FUTUROS ({projs.length})</div>
        {projs.map((p,i)=>{const prob=p.probability||0.85,pc=prob>=0.85?B.green:prob>=0.7?B.yellow:B.red;return(<div key={i} style={{padding:"10px 14px",borderBottom:i<projs.length-1?"1px solid "+B.border:"none"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><div><span style={{fontSize:12,fontWeight:700,color:B.text}}>{p.channel||"MP"}</span>{p.source&&<span style={{fontSize:9,color:B.textMut,marginLeft:6,background:B.bg,padding:"1px 4px",borderRadius:3}}>{p.source}</span>}<span style={{fontSize:10,color:B.textMut,marginLeft:8}}>{p.date}</span></div><span style={{fontSize:10,fontWeight:700,color:pc}}>{fmtP(prob*100)}</span></div>
          {p.description&&<div style={{fontSize:10,color:B.textSub,marginBottom:4}}>{p.description}{p.situacao&&<span style={{marginLeft:6,fontSize:9,color:B.textMut,background:B.bg,padding:"1px 5px",borderRadius:4}}>{p.situacao}</span>}</div>}
          <div style={{display:"flex",gap:16}}><div><div style={{fontSize:9,color:B.textMut,fontWeight:600}}>ESPERADO</div><div style={{fontSize:12,color:B.blue,fontFamily:"monospace",fontWeight:600}}>{fmt(p.expectedRevenue)}</div></div><div><div style={{fontSize:9,color:B.textMut,fontWeight:600}}>PONDERADO</div><div style={{fontSize:12,color:B.green,fontFamily:"monospace",fontWeight:700}}>{fmt((p.expectedRevenue||0)*prob)}</div></div></div>
        </div>);})}
      </Card>
    </div>
  );
}

// CONFIG SCREEN
function ConfigScreen({appLogoUrl,setAppLogoUrl,companies,setCompanies,activeCompany,setActiveCompany,txs,brief,addMsg}){
  const [configTab, setConfigTab] = useState("empresa"); // empresa | categorias
  const activeComp = companies.find(c=>c.id===activeCompany)||null;
  const compBrief = activeComp?.brief||"";
  const compCmvList = activeComp?.cmvList||[];
  const [lb,setLb]=useState(compBrief);
  const [newP,setNewP]=useState(""); const [newV,setNewV]=useState("");
  const [saved,setSaved]=useState(false);

  // Sync lb when active company changes
  useEffect(()=>{ setLb(activeComp?.brief||""); },[activeCompany]);

  const updateCompany = (fn) => setCompanies(prev=>prev.map(c=>c.id===activeCompany?fn(c):c));
  const save = () => { updateCompany(c=>({...c,brief:lb})); setSaved(true); setTimeout(()=>setSaved(false),2000); };
  const addCMV = () => {
    if(!newP||!newV) return;
    const v=parseFloat(newV.replace(/\./g,"").replace(",","."))||0; if(!v) return;
    updateCompany(c=>({...c,cmvList:[...(c.cmvList||[]).filter(x=>x.period!==newP),{period:newP,value:v}].sort((a,b)=>a.period.localeCompare(b.period))}));
    setNewP(""); setNewV("");
  };

  const handleLogoUpload = async e => {
    const file = e.target.files[0]; if(!file) return;
    const b64 = await toB64(file);
    setAppLogoUrl("data:"+file.type+";base64,"+b64);
  };

  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyDone, setNewCompanyDone] = useState(null);
  const addCompany = () => {
    const name = newCompanyName.trim();
    if (!name) return;
    const newC = {id:genId(), name, clientPassword: Math.random().toString(36).slice(2,8).toUpperCase()};
    setCompanies(prev=>[...prev, newC]);
    setActiveCompany(newC.id);
    setNewCompanyName("");
    setNewCompanyDone(newC);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      {/* Sub-tabs */}
      <div style={{display:"flex",background:B.card,borderBottom:"1px solid "+B.border,padding:"0 clamp(12px,2vw,20px)",gap:0,flexShrink:0}}>
        {[{key:"empresa",label:"🏢 Empresa & CMV"},{key:"categorias",label:"🏷️ Categorias"}].map(({key,label})=>(
          <button key={key} onClick={()=>setConfigTab(key)} style={{padding:"12px 16px",border:"none",borderBottom:"3px solid "+(configTab===key?B.blue:"transparent"),background:"transparent",color:configTab===key?B.blue:B.textSub,fontSize:12,fontWeight:configTab===key?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>{label}</button>
        ))}
      </div>

      <div style={{flex:1,overflowY:"auto"}}>
        {configTab==="empresa" && (
          <div style={{padding:"0 clamp(12px,2vw,20px) 32px"}}>
            {/* Logo upload */}
            <Card title="🖼️ LOGO DO APLICATIVO" accent={B.blue}>
              <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                {appLogoUrl && <img src={appLogoUrl} alt="Logo" style={{height:50,maxWidth:180,objectFit:"contain",border:"1px solid "+B.border,borderRadius:8,padding:4}}/>}
                <label style={{background:B.blue,border:"none",borderRadius:10,padding:"10px 16px",color:"white",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={handleLogoUpload}/>
                  {appLogoUrl?"🔄 Trocar Logo":"📤 Fazer Upload da Logo"}
                </label>
                {appLogoUrl&&<button onClick={()=>setAppLogoUrl("")} style={{background:"transparent",border:"1px solid "+B.border2,borderRadius:8,padding:"8px 12px",color:B.textMut,fontSize:11,cursor:"pointer"}}>Remover</button>}
              </div>
              <div style={{fontSize:10,color:B.textMut,marginTop:8}}>Formatos: PNG, JPG, SVG. Recomendado: fundo transparente.</div>
            </Card>

            {/* Company selector */}
            <Card title="🏢 EMPRESAS / PORTAIS DE CLIENTES" accent={B.green}>
              <div style={{marginBottom:12}}>
                {companies.length===0 && <div style={{fontSize:12,color:B.textMut,marginBottom:10}}>Nenhuma empresa cadastrada. Adicione uma empresa para criar um portal de cliente.</div>}
                {companies.map(c=>(
                  <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:activeCompany===c.id?B.green+"10":B.bg,borderRadius:10,marginBottom:6,border:"1px solid "+(activeCompany===c.id?B.green:B.border)}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:B.dark}}>{c.name}</div>
                      <div style={{fontSize:10,color:B.textMut}}>Senha portal: <strong style={{color:B.blue,fontFamily:"monospace"}}>{c.clientPassword}</strong></div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>setActiveCompany(c.id)} style={{fontSize:10,background:activeCompany===c.id?B.green:B.card,border:"1px solid "+(activeCompany===c.id?B.green:B.border),borderRadius:8,padding:"4px 10px",color:activeCompany===c.id?"white":B.textSub,cursor:"pointer",fontWeight:600}}>{activeCompany===c.id?"✓ Ativo":"Ativar"}</button>
                      <button onClick={()=>setCompanies(prev=>prev.filter(x=>x.id!==c.id))} style={{fontSize:10,background:B.red+"12",border:"1px solid "+B.red+"44",borderRadius:8,padding:"4px 10px",color:B.red,cursor:"pointer",fontWeight:600}}>Excluir</button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Add company inline form */}
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <input value={newCompanyName} onChange={e=>setNewCompanyName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCompany()} placeholder="Nome da empresa (ex: BTH Style)"
                  style={{flex:1,padding:"10px 12px",background:B.bg,border:"1px solid "+B.border2,borderRadius:10,fontSize:13,color:B.dark}}/>
                <button onClick={addCompany} disabled={!newCompanyName.trim()} style={{background:newCompanyName.trim()?B.green:"#9AA0A6",border:"none",borderRadius:10,padding:"10px 16px",color:"white",fontSize:12,fontWeight:700,cursor:newCompanyName.trim()?"pointer":"default",flexShrink:0}}>+ Adicionar</button>
              </div>
              {newCompanyDone&&(
                <div style={{background:"#E8F5E9",border:"1px solid "+B.green,borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:B.green,marginBottom:6}}>✅ Empresa criada com sucesso!</div>
                  <div style={{fontSize:12,color:B.dark,marginBottom:4}}><strong>{newCompanyDone.name}</strong></div>
                  <div style={{fontSize:11,color:B.textSub,marginBottom:8}}>Senha do portal do cliente: <strong style={{fontFamily:"monospace",fontSize:16,color:B.blue,letterSpacing:"0.1em"}}>{newCompanyDone.clientPassword}</strong></div>
                  <div style={{background:B.blue+"12",border:"1px solid "+B.blue+"44",borderRadius:8,padding:"10px 12px",fontSize:11,color:B.dark,lineHeight:1.7}}>
                    <strong style={{color:B.blue}}>📱 Como o cliente acessa:</strong><br/>
                    1. Acesse o mesmo link deste app<br/>
                    2. Clique em <strong>"Portal do Cliente"</strong><br/>
                    3. Digite a senha: <strong style={{fontFamily:"monospace",color:B.blue}}>{newCompanyDone.clientPassword}</strong><br/>
                    <br/>
                    <strong style={{color:"#E65100"}}>⚠️ Esta senha é única — anote antes de fechar!</strong>
                  </div>
                  <button onClick={()=>setNewCompanyDone(null)} style={{marginTop:8,width:"100%",background:"transparent",border:"1px solid "+B.border2,borderRadius:8,padding:"6px",color:B.textSub,fontSize:11,cursor:"pointer"}}>OK, entendi</button>
                </div>
              )}
              <div style={{fontSize:10,color:B.textMut,marginTop:4,lineHeight:1.5}}>
                Cada empresa tem uma senha única. O cliente acessa o portal usando <strong>o mesmo link</strong> deste app e digitando a senha fornecida. Ele verá apenas relatórios, sem poder editar.
              </div>
              <div style={{background:B.blue+"10",border:"1px solid "+B.blue+"44",borderRadius:10,padding:"12px 14px",marginTop:8}}>
                <div style={{fontSize:12,fontWeight:700,color:B.blue,marginBottom:6}}>🔗 Como enviar o link ao cliente:</div>
                <div style={{fontSize:11,color:B.dark,lineHeight:1.9}}>
                  1. Copie a URL desta página no seu navegador<br/>
                  2. Envie a URL + a senha da empresa por WhatsApp/e-mail<br/>
                  3. O cliente abre o link, clica em <strong>"Portal do Cliente"</strong> e digita a senha
                </div>
                <div style={{fontSize:10,color:"#795548",marginTop:8,background:"#FFF8E1",borderRadius:8,padding:"8px 10px",border:"1px solid #F9AB00"}}>
                  💾 Os dados são salvos automaticamente neste browser. O cliente precisa acessar <strong>o mesmo link</strong> para ver os dados que você importou.
                </div>
              </div>
            </Card>

            {/* Briefing */}
            <Card title="📋 BRIEFING DA EMPRESA" accent={B.blue}>
              <p style={{fontSize:"clamp(11px,1.3vw,13px)",color:B.textSub,lineHeight:1.6,marginBottom:10}}>Descreva a empresa para que a IA categorize melhor e dê análises mais precisas.</p>
              <textarea value={lb} onChange={e=>setLb(e.target.value)} disabled={!activeComp} placeholder={activeComp?"Ex: Ecommerce B2B de moda importada. Vendemos para lojistas via Mercado Pago...":"Selecione uma empresa acima para editar o briefing"}
                style={{width:"100%",background:B.bg,border:"1px solid "+B.border2,borderRadius:10,padding:"12px",color:B.text,fontSize:"clamp(11px,1.3vw,13px)",lineHeight:1.6,resize:"vertical",minHeight:120}}/>
              {!activeComp&&<div style={{fontSize:11,color:B.red,marginBottom:8}}>⚠️ Selecione uma empresa ativa acima para editar briefing e CMV</div>}
              {lb&&<div style={{fontSize:10,color:B.blue,marginTop:6,fontWeight:600}}>✓ {lb.split(" ").length} palavras · empresa: {activeComp?.name}</div>}
            </Card>

            {/* CMV */}
            <Card title="📦 CMV POR PERÍODO — via ERP" accent={B.yellow}>
              <div style={{background:"#FFF8E1",borderRadius:10,padding:"10px 12px",marginBottom:12,fontSize:"clamp(10px,1.2vw,12px)",color:"#795548",lineHeight:1.6}}>
                <strong style={{color:"#E65100"}}>Como obter:</strong> Omie → Relatórios → DRE → valor do CMV
              </div>
              {compCmvList.map(c=>(<div key={c.period} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:B.bg,borderRadius:8,marginBottom:6,border:"1px solid "+B.border}}>
                <div><div style={{fontSize:11,fontWeight:700,color:B.text}}>{c.period.split("-").reverse().join("/")}</div><div style={{fontSize:13,fontWeight:800,color:B.yellow,fontFamily:"monospace"}}>{fmt(c.value)}</div></div>
                <button onClick={()=>updateCompany(co=>({...co,cmvList:(co.cmvList||[]).filter(x=>x.period!==c.period)}))} style={{background:B.red+"18",border:"1px solid "+B.red+"44",borderRadius:8,padding:"4px 10px",color:B.red,fontSize:11,cursor:"pointer",fontWeight:700}}>Remover</button>
              </div>))}
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <input type="month" value={newP} onChange={e=>setNewP(e.target.value)} style={{flex:1,background:B.bg,border:"1px solid "+B.border2,borderRadius:8,padding:"9px 10px",color:B.text,fontSize:12,colorScheme:"light"}}/>
                <input type="text" inputMode="decimal" value={newV} onChange={e=>setNewV(e.target.value)} placeholder="Ex: 126.600,00" style={{flex:1.5,background:B.bg,border:"1px solid "+B.border2,borderRadius:8,padding:"9px 10px",color:B.text,fontSize:12,fontFamily:"monospace"}}/>
              </div>
              <button onClick={addCMV} disabled={!activeComp} style={{width:"100%",background:activeComp?B.yellow:"#9AA0A6",border:"none",borderRadius:10,padding:"10px",color:"white",fontSize:12,fontWeight:700,cursor:activeComp?"pointer":"not-allowed"}}>{activeComp?"+ Adicionar CMV do Período":"Selecione uma empresa acima"}</button>
            </Card>

            <button onClick={save} disabled={!activeComp} style={{width:"100%",background:saved?B.green:activeComp?B.blue:"#9AA0A6",border:"none",borderRadius:12,padding:"14px",color:"white",fontSize:"clamp(12px,1.5vw,15px)",fontWeight:700,cursor:activeComp?"pointer":"not-allowed",transition:"all 0.3s"}}>{saved?"✅ Salvo!":activeComp?"💾 Salvar Configurações":"Selecione uma empresa para salvar"}</button>
          </div>
        )}
        {configTab==="categorias" && (
          <CategoryManager company={activeComp} companies={companies} setCompanies={setCompanies} activeCompany={activeCompany} txs={txs} brief={brief} addMsg={addMsg}/>
        )}
      </div>
    </div>
  );
}

// CHAT SCREEN
function ChatScreen({messages,inputMsg,setInputMsg,sendMessage,isTyping,chatEndRef}){
  const quick=["Analise as margens","Maiores despesas","Saúde do caixa","CMV correto?","Sugestões práticas","Quando o caixa pode acabar?"];
  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",background:B.bg}}>
      <div style={{flex:1,overflowY:"auto",padding:"clamp(10px,1.5vw,16px)"}}>
        {messages.map((msg,i)=>(
          <div key={i} style={{marginBottom:14,display:"flex",flexDirection:msg.from==="client"||msg.role==="user"?"row-reverse":"row",gap:8,alignItems:"flex-end"}}>
            <div style={{width:32,height:32,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,background:msg.from==="client"?"#FF6D00":msg.role==="user"?B.blue:B.green,color:"white",fontWeight:700}}>{msg.from==="client"?"C":msg.role==="user"?"A":"S"}</div>
            <div style={{maxWidth:"80%",padding:"clamp(9px,1.2vw,12px) clamp(11px,1.5vw,14px)",borderRadius:msg.role==="user"?"16px 4px 16px 16px":"4px 16px 16px 16px",background:msg.from==="client"?"#FF6D00":msg.role==="user"?B.blue:B.card,border:(msg.from==="client"||msg.role==="user")?"none":"1px solid "+B.border,fontSize:"clamp(12px,1.4vw,14px)",lineHeight:1.65,color:(msg.from==="client"||msg.role==="user")?"white":B.text,boxShadow:"0 1px 3px rgba(0,0,0,0.08)",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
              {msg.content.split("**").map((part,j)=>j%2===1?<strong key={j}>{part}</strong>:part)}
              {msg.from==="client"&&<div style={{fontSize:9,color:"rgba(255,255,255,0.75)",marginTop:3,textAlign:"right"}}>👤 Cliente</div>}
              {msg.from==="admin"&&msg.role==="user"&&<div style={{fontSize:9,color:"rgba(255,255,255,0.75)",marginTop:3,textAlign:"right"}}>🔧 Admin</div>}
            </div>
          </div>
        ))}
        {isTyping&&(<div style={{display:"flex",gap:8,alignItems:"flex-end"}}><div style={{width:32,height:32,borderRadius:"50%",background:B.green,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:700}}>S</div><div style={{padding:"12px 16px",background:B.card,border:"1px solid "+B.border,borderRadius:"4px 16px 16px 16px"}}><span style={{fontSize:20,animation:"blink 0.8s infinite",display:"inline-block",color:B.textMut}}>···</span></div></div>)}
        <div ref={chatEndRef}/>
      </div>
      <div style={{padding:"7px 14px 5px",overflowX:"auto",display:"flex",gap:5,whiteSpace:"nowrap",borderTop:"1px solid "+B.border,background:B.card}}>
        {quick.map(q=><button key={q} onClick={()=>setInputMsg(q)} style={{fontSize:"clamp(10px,1.1vw,12px)",background:B.bg,border:"1px solid "+B.border2,borderRadius:20,padding:"5px 12px",color:B.textSub,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{q}</button>)}
      </div>
      <div style={{display:"flex",gap:10,padding:"9px 14px 14px",borderTop:"1px solid "+B.border,background:B.card}}>
        <input style={{flex:1,background:B.bg,border:"1px solid "+B.border2,borderRadius:24,padding:"11px 16px",color:B.text,fontSize:"clamp(12px,1.4vw,14px)"}}
          placeholder="Pergunte sobre suas finanças..."
          value={inputMsg} onChange={e=>setInputMsg(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()}/>
        <button onClick={sendMessage} disabled={isTyping} style={{width:44,height:44,background:isTyping?B.border:B.blue,border:"none",borderRadius:"50%",color:"white",cursor:isTyping?"default":"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>↑</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLIENT PORTAL — Read-only view for clients
// ═══════════════════════════════════════════════════════════════
function ClientPortal({allTxs,projs,balanceByDate,months,period,setPeriod,company,appLogoUrl,onLogout}){
  const [tab,setTab]=useState("chat");
  const companyCmvList = company?.cmvList||[];
  const data = useMemo(()=>(allTxs.length||projs.length)?processData(allTxs,projs,companyCmvList,period,balanceByDate,company?.id||"default"):null,[allTxs,projs,company,period,balanceByDate]);

  // Portal Chat IA state
  const [portalMessages,setPortalMessages] = useState([{role:"assistant",content:"Olá! Sou o **SmartCFO**, seu CFO Virtual. 🚀\n\nEstou aqui para analisar as finanças de **"+(company?.name||"sua empresa")+"** e responder suas dúvidas.\n\nPode perguntar sobre margens, fluxo de caixa, despesas, projeções ou qualquer aspecto financeiro do negócio!"}]);
  useEffect(()=>{
    (async()=>{
      try {
        const key = "smartcfo:chat:"+(company?.id||"default");
        const r = await window.storage.get(key, true);
        const saved = r ? JSON.parse(r.value) : [];
        if(saved.length>0) setPortalMessages(prev=>[prev[0],...saved]);
      } catch(e) {}
    })();
  },[company?.id]);
  const [portalInput,setPortalInput] = useState("");
  const [portalTyping,setPortalTyping] = useState(false);
  const portalChatRef = useRef(null);
  useEffect(()=>{ portalChatRef.current?.scrollIntoView({behavior:"smooth"}); },[portalMessages]);

  const portalSend = async () => {
    if(!portalInput.trim()||portalTyping) return;
    const msg = portalInput.trim(); setPortalInput(""); setPortalMessages(prev=>[...prev,{role:"user",content:msg,from:"client",ts:Date.now()}]); setPortalTyping(true);
    const d = data?.dre; const rw = data?.runway;
    const rwLine = rw?("Saldo: "+fmt(rw.lastBalance)+" | Queima/dia: "+fmt(rw.avgDailyBurn)+(rw.zeroDay?" | ALERTA caixa: "+rw.zeroDay:"")):"";
    const brief = company?.brief||"";
    const ctx = d?("Empresa: "+(company?.name||"ecommerce")+(brief?" | "+brief:"")+"\nReceita Líquida: "+fmt(d.netRev)+" | Lucro Bruto: "+fmt(d.gProfit)+" ("+fmtP(d.gMargin)+")\nCMV: "+fmt(d.cogs)+" | Pessoal: "+fmt(d.pers)+" | Mkt: "+fmt(d.mkt)+" | Fixas: "+fmt(d.fix)+" | OPEX: "+fmt(d.opex)+"\nEBITDA: "+fmt(d.ebitda)+" ("+fmtP(d.ebitdaM)+")\n"+rwLine):"Aguardando dados do administrador.";
    const sys = "Você é o SmartCFO, CFO Virtual especializado em ecommerce brasileiro. Responda em português, de forma clara, objetiva e acionável. Use **negrito** para números. Seja consultor financeiro de alto nível.\n\nCONTEXTO:\n"+ctx;
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:1024,system:sys,messages:[{role:"user",content:msg}]})});
      const raw = await res.text();
      console.log("Portal API:", res.status, raw.slice(0,200));
      let resp; try{resp=JSON.parse(raw);}catch{throw new Error("Parse falhou ("+res.status+"): "+raw.slice(0,300));}
      if(!res.ok||resp.error) throw new Error(resp.error?.message||resp.error?.type||("HTTP "+res.status+" "+raw.slice(0,200)));
      const reply=(resp.content||[]).find(b=>b.type==="text")?.text;
      setPortalTyping(false); setPortalMessages(prev=>[...prev,{role:"assistant",content:reply||"Sem resposta."}]);
    } catch(e){ setPortalTyping(false); setPortalMessages(prev=>[...prev,{role:"assistant",content:"⚠️ Erro: "+e.message}]); }
  };

  const portalNav = [
    {key:"chat",    icon:"💬", label:"CFO Virtual", hero:true},
    {key:"home",    icon:"◈",  label:"Painel"},
    {key:"cashflow",icon:"↕",  label:"Fluxo de Caixa"},
    {key:"dre",     icon:"≡",  label:"DRE"},
    {key:"runway",  icon:"💧", label:"Projeção Caixa"},
    {key:"proj",    icon:"📈", label:"A Receber"},
  ];

  return(
    <div style={{minHeight:"100dvh",display:"flex",flexDirection:"column",background:B.bg,fontFamily:"'Google Sans','Roboto',sans-serif",color:B.text}}>
      {/* Portal Header */}
      <div style={{background:B.card,borderBottom:"1px solid "+B.border,padding:"12px clamp(16px,3vw,32px)",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {appLogoUrl && <img src={appLogoUrl} alt="Logo" style={{height:"clamp(28px,3.5vw,40px)",width:"auto",objectFit:"contain"}}/>}
          <div>
            <div style={{fontSize:"clamp(13px,1.6vw,16px)",fontWeight:700,color:B.dark}}>{company?.name||"Relatório Financeiro"}</div>
            <div style={{fontSize:9,color:B.textMut,letterSpacing:"0.05em"}}>PORTAL DO CLIENTE · SOMENTE LEITURA</div>
          </div>
        </div>
        <button onClick={onLogout} style={{fontSize:11,background:"transparent",border:"1px solid "+B.border2,borderRadius:8,padding:"6px 12px",color:B.textSub,cursor:"pointer"}}>Sair</button>
      </div>

      {/* Tabs */}
      <div style={{background:B.card,borderBottom:"1px solid "+B.border,padding:"0 clamp(12px,2vw,24px)",display:"flex",gap:0,overflowX:"auto",alignItems:"stretch"}}>
        {portalNav.map(({key,icon,label,hero})=>(
          <button key={key} onClick={()=>setTab(key)} style={{padding:hero?"14px 20px":"12px 14px",border:"none",borderBottom:"3px solid "+(tab===key?B.blue:"transparent"),background:hero&&tab===key?"linear-gradient(135deg,"+B.blue+"15,"+B.green+"10)":hero?"transparent":"transparent",color:tab===key?B.blue:B.textSub,fontSize:hero?"clamp(12px,1.5vw,14px)":"clamp(10px,1.2vw,12px)",fontWeight:tab===key?800:hero?600:400,cursor:"pointer",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:hero?8:5,position:"relative"}}>
            <span style={{fontSize:hero?"clamp(16px,1.8vw,20px)":"clamp(13px,1.5vw,15px)"}}>{icon}</span>
            <span>{label}</span>
            {hero&&<span style={{fontSize:9,background:B.green,color:"white",borderRadius:8,padding:"1px 6px",fontWeight:700,marginLeft:2}}>IA</span>}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflow:tab==="chat"?"hidden":"auto",display:"flex",flexDirection:"column"}}>
        {/* Period filter always visible */}
        {months.length>0&&(<div style={{padding:"clamp(12px,2vw,20px) clamp(12px,2vw,20px) 0"}}><PeriodFilter months={months} selected={period} onChange={setPeriod}/></div>)}

        {tab==="chat" && (
          <div style={{display:"flex",flexDirection:"column",height:"100%",background:B.bg}}>
            {/* Chat hero header */}
            <div style={{background:"linear-gradient(135deg,"+B.blue+","+B.green+")",padding:"clamp(16px,2.5vw,28px)",color:"white"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                <div style={{width:48,height:48,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🤖</div>
                <div>
                  <div style={{fontSize:"clamp(16px,2vw,22px)",fontWeight:800,letterSpacing:"-0.02em"}}>CFO Virtual IA</div>
                  <div style={{fontSize:"clamp(10px,1.2vw,12px)",opacity:0.85}}>Análise financeira inteligente para {company?.name||"sua empresa"}</div>
                </div>
                <div style={{marginLeft:"auto",background:"rgba(255,255,255,0.2)",borderRadius:20,padding:"4px 12px",fontSize:10,fontWeight:700}}>● Online</div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {["Analise minhas margens","Quando o caixa acaba?","Maiores despesas","Sugestões de melhoria"].map(q=>(
                  <button key={q} onClick={()=>setPortalInput(q)} style={{background:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:20,padding:"5px 12px",color:"white",fontSize:"clamp(10px,1.2vw,12px)",cursor:"pointer",fontWeight:500,whiteSpace:"nowrap"}}>{q}</button>
                ))}
              </div>
            </div>
            {/* Messages */}
            <div style={{flex:1,overflowY:"auto",padding:"clamp(12px,2vw,20px)"}}>
              {portalMessages.map((msg,i)=>(
                <div key={i} style={{marginBottom:14,display:"flex",flexDirection:msg.role==="user"?"row-reverse":"row",gap:8,alignItems:"flex-end"}}>
                  <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,background:msg.role==="user"?B.blue:B.green,color:"white",fontWeight:700}}>{msg.role==="user"?"U":"🤖"}</div>
                  <div style={{maxWidth:"82%",padding:"clamp(10px,1.4vw,13px) clamp(12px,1.6vw,16px)",borderRadius:msg.role==="user"?"18px 4px 18px 18px":"4px 18px 18px 18px",background:msg.role==="user"?"linear-gradient(135deg,"+B.blue+",#0D47A1)":B.card,border:msg.role==="user"?"none":"1px solid "+B.border,fontSize:"clamp(12px,1.4vw,14px)",lineHeight:1.7,color:msg.role==="user"?"white":B.text,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                    {msg.content.split("**").map((part,j)=>j%2===1?<strong key={j} style={{color:msg.role==="user"?"white":B.dark}}>{part}</strong>:part)}
                  </div>
                </div>
              ))}
              {portalTyping&&(<div style={{display:"flex",gap:8,alignItems:"flex-end"}}><div style={{width:34,height:34,borderRadius:"50%",background:B.green,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:14}}>🤖</div><div style={{padding:"12px 18px",background:B.card,border:"1px solid "+B.border,borderRadius:"4px 18px 18px 18px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}><span style={{fontSize:20,animation:"blink 0.8s infinite",display:"inline-block",color:B.textMut}}>···</span></div></div>)}
              <div ref={portalChatRef}/>
            </div>
            {/* Input */}
            <div style={{padding:"clamp(10px,1.5vw,16px)",borderTop:"1px solid "+B.border,background:B.card,boxShadow:"0 -2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
                <textarea value={portalInput} onChange={e=>setPortalInput(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();portalSend();}}}
                  placeholder="Pergunte sobre as finanças da empresa... (Enter para enviar)"
                  rows={1} style={{flex:1,background:B.bg,border:"1px solid "+B.border2,borderRadius:16,padding:"12px 16px",color:B.text,fontSize:"clamp(12px,1.4vw,14px)",resize:"none",lineHeight:1.4,minHeight:46,maxHeight:120,overflow:"auto"}}/>
                <button onClick={portalSend} disabled={portalTyping||!portalInput.trim()}
                  style={{width:46,height:46,background:portalTyping||!portalInput.trim()?"#E8EAED":"linear-gradient(135deg,"+B.blue+","+B.green+")",border:"none",borderRadius:"50%",color:"white",cursor:portalTyping||!portalInput.trim()?"default":"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:portalInput.trim()?"0 4px 12px rgba(26,115,232,0.4)":"none",transition:"all 0.2s"}}>↑</button>
              </div>
            </div>
          </div>
        )}
        {tab==="home"  && data && <HomeScreen data={data} months={months} period={period} setPeriod={setPeriod} uncategorized={0} onGoTo={setTab} appLogo={appLogoUrl}/>}
        {tab==="dre"   && <DREScreen data={data} months={months} period={period} setPeriod={setPeriod}/>}
        {tab==="cashflow" && <CashflowScreen data={data} months={months} period={period} setPeriod={setPeriod}/>}
        {tab==="runway" && <RunwayScreen runway={data?.runway}/>}
        {tab==="proj"  && <ProjectionsScreen data={data}/>}
        {!data && tab!=="chat" && <div style={{padding:"60px 20px",textAlign:"center",color:B.textMut,fontSize:14}}><div style={{fontSize:52,marginBottom:16}}>📊</div>Aguardando dados do administrador.<br/><span style={{fontSize:12,color:B.textMut}}>A IA já está disponível para responder suas dúvidas!</span></div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOGIN / PORTAL SELECTOR
// ═══════════════════════════════════════════════════════════════
function LoginScreen({companies, onAdminLogin, onClientLogin, appLogoUrl}){
  const [mode,setMode]=useState("select"); // select | admin | client
  const [clientPwd,setClientPwd]=useState("");
  const [err,setErr]=useState("");

  const tryClientLogin = () => {
    const company = companies.find(c=>c.clientPassword===clientPwd.trim().toUpperCase());
    if(company) { onClientLogin(company); setErr(""); }
    else setErr("Senha inválida. Verifique com seu consultor.");
  };

  return(
    <div style={{minHeight:"100dvh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:B.bg,padding:"20px",fontFamily:"'Google Sans','Roboto',sans-serif"}}>
      <div style={{width:"100%",maxWidth:400}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:32}}>
          {appLogoUrl
            ? <img src={appLogoUrl} alt="Logo" style={{height:60,maxWidth:240,objectFit:"contain"}}/>
            : <div style={{display:"inline-flex",alignItems:"center",gap:10}}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <path d="M10 4 Q10 12 18 12" stroke={B.red}   strokeWidth="4" strokeLinecap="round" fill="none"/>
                  <path d="M6 10 Q6 20 14 20" stroke={B.blue}  strokeWidth="4" strokeLinecap="round" fill="none"/>
                  <path d="M14 18 Q14 28 22 28" stroke={B.green}  strokeWidth="4" strokeLinecap="round" fill="none"/>
                  <path d="M10 24 Q10 32 18 32" stroke={B.yellow} strokeWidth="4" strokeLinecap="round" fill="none"/>
                </svg>
                <div><span style={{fontSize:22,fontWeight:400,color:B.textSub}}>Smart</span><span style={{fontSize:22,fontWeight:800,color:B.dark}}>CFO</span></div>
              </div>
          }
          <div style={{fontSize:12,color:B.textMut,marginTop:8}}>Inteligência Financeira para E-commerce</div>
        </div>

        {mode==="select"&&(
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <button onClick={()=>setMode("admin")} style={{padding:"16px",background:B.blue,border:"none",borderRadius:14,color:"white",fontSize:15,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:24}}>🔧</span>
              <div style={{textAlign:"left"}}><div>Acesso Administrador</div><div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Importar dados, categorizar, configurar</div></div>
            </button>
            <button onClick={()=>setMode("client")} style={{padding:"16px",background:B.green,border:"none",borderRadius:14,color:"white",fontSize:15,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:24}}>👀</span>
              <div style={{textAlign:"left"}}><div>Portal do Cliente</div><div style={{fontSize:11,fontWeight:400,opacity:0.85}}>Visualizar relatórios financeiros</div></div>
            </button>
          </div>
        )}

        {mode==="admin"&&(
          <div style={{background:B.card,borderRadius:16,padding:24,boxShadow:"0 2px 12px rgba(0,0,0,0.08)"}}>
            <div style={{fontSize:15,fontWeight:700,color:B.dark,marginBottom:4}}>🔧 Acesso Administrador</div>
            <div style={{fontSize:12,color:B.textMut,marginBottom:16}}>Acesso completo ao sistema</div>
            <button onClick={onAdminLogin} style={{width:"100%",padding:"13px",background:B.blue,border:"none",borderRadius:12,color:"white",fontSize:14,fontWeight:700,cursor:"pointer"}}>Entrar como Administrador</button>
            <button onClick={()=>setMode("select")} style={{width:"100%",marginTop:10,padding:"10px",background:"transparent",border:"1px solid "+B.border2,borderRadius:12,color:B.textSub,fontSize:12,cursor:"pointer"}}>← Voltar</button>
          </div>
        )}

        {mode==="client"&&(
          <div style={{background:B.card,borderRadius:16,padding:24,boxShadow:"0 2px 12px rgba(0,0,0,0.08)"}}>
            <div style={{fontSize:15,fontWeight:700,color:B.dark,marginBottom:4}}>👀 Portal do Cliente</div>
            <div style={{fontSize:12,color:B.textMut,marginBottom:16}}>Digite a senha fornecida pelo seu consultor</div>
            <input value={clientPwd} onChange={e=>setClientPwd(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&tryClientLogin()} placeholder="EX: AB12CD" maxLength={8}
              style={{width:"100%",padding:"13px 16px",background:B.bg,border:"1px solid "+(err?B.red:B.border2),borderRadius:12,fontSize:18,fontFamily:"monospace",fontWeight:700,color:B.dark,letterSpacing:"0.15em",textAlign:"center",marginBottom:10}}/>
            {err&&<div style={{fontSize:11,color:B.red,marginBottom:10,textAlign:"center"}}>{err}</div>}
            {companies.length===0&&<div style={{fontSize:11,color:B.textMut,marginBottom:10,textAlign:"center"}}>Nenhuma empresa cadastrada ainda. Configure na aba Configurações.</div>}
            <button onClick={tryClientLogin} style={{width:"100%",padding:"13px",background:B.green,border:"none",borderRadius:12,color:"white",fontSize:14,fontWeight:700,cursor:"pointer"}}>Acessar Portal</button>
            <button onClick={()=>setMode("select")} style={{width:"100%",marginTop:10,padding:"10px",background:"transparent",border:"1px solid "+B.border2,borderRadius:12,color:B.textSub,fontSize:12,cursor:"pointer"}}>← Voltar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════
export default function App(){
  // Auth state
  const [authMode, setAuthMode]         = useState("login"); // login | admin | client
  const [clientCompany, setClientCompany] = useState(null);

  // App state
  const [txs,setTxs]                   = useState([]);
  const [projs,setProjs]               = useState([]);
  const [balanceByDate,setBalanceByDate] = useState({});
  const [transfersPending,setTransfersPending] = useState(null);
  const [loading,setLoading]           = useState({});
  const [period,setPeriod]             = useState("");
  const [appLogoUrl,setAppLogoUrl]     = useState("");
  const [companies,setCompanies]       = useState([]);
  const [activeCompany,setActiveCompany] = useState(null);
  const activeCompanyRef = useRef(null);
  useEffect(()=>{ activeCompanyRef.current = activeCompany; }, [activeCompany]);

  const [screen,setScreen]             = useState("import");
  const [messages,setMessages]         = useState([{role:"assistant",content:"Olá! Sou o **SmartCFO**. 🚀\n\nComo começar:\n1️⃣ **Configurar** → Logo + empresa + CMV do ERP\n2️⃣ **Importar** → Extratos MP e Omie Cash\n3️⃣ **Categorizar** → Revise com ajuda da IA\n4️⃣ **Painel** → Veja resultados e projeção de caixa\n\nDRE: toque 1x para subcategorias, 2x para lançamentos!"}]);
  const [inputMsg,setInputMsg]         = useState("");
  const [isTyping,setIsTyping]         = useState(false);
  const chatEndRef                     = useRef(null);

  // Derive brief and cmvList from the active company — single source of truth
  const activeComp = useMemo(()=>companies.find(c=>c.id===activeCompany)||null,[companies,activeCompany]);
  const brief   = activeComp?.brief||"";
  const cmvList = activeComp?.cmvList||[];

  const months = useMemo(()=>{
    const cId = activeCompany||"default";
    const filtered = txs.filter(t=>(t.companyId||"default")===cId);
    return [...new Set(filtered.map(t=>ym(t.competenceDate||t.date)).filter(Boolean))].sort();
  },[txs,activeCompany]);
  const data   = useMemo(()=>(txs.length||projs.length)?processData(txs,projs,cmvList,period,balanceByDate,activeCompany||"default"):null,[txs,projs,cmvList,period,balanceByDate,activeCompany]);

  // ── PERSISTENT STORAGE ──────────────────────────────────────
  const [storageReady, setStorageReady] = useState(false);

  // Load all data from storage on mount
  useEffect(()=>{
    (async()=>{
      try {
        const load = async(key, def) => {
          try { const r = await window.storage.get(key, true); return r ? JSON.parse(r.value) : def; }
          catch { return def; }
        };
        const [sTxs, sProjs, sBal, sCompanies, sLogo, sActive] = await Promise.all([
          load("smartcfo:txs",[]), load("smartcfo:projs",[]), load("smartcfo:bal",{}),
          load("smartcfo:companies",[]),
          load("smartcfo:logo",""), load("smartcfo:activeCompany",null),
        ]);
        if(sTxs.length) setTxs(sTxs);
        if(sProjs.length) setProjs(sProjs);
        if(Object.keys(sBal).length) setBalanceByDate(sBal);
        if(sCompanies.length) setCompanies(sCompanies);
        if(sLogo) setAppLogoUrl(sLogo);
        if(sActive) setActiveCompany(sActive);
      } catch(e) { console.warn("Storage load error:", e); }
      setStorageReady(true);
    })();
  },[]);

  // Save data to storage whenever it changes (debounced)
  const saveToStorage = useCallback(async(key, value) => {
    try { await window.storage.set(key, JSON.stringify(value), true); }
    catch(e) { console.warn("Storage save error:", e); }
  },[]);

  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:txs", txs); },[txs, storageReady]);
  useEffect(()=>{
    if(storageReady) {
      const key = "smartcfo:chat:"+(activeCompany||"default");
      // Only persist non-welcome messages
      const toSave = messages.filter(m=>m.ts);
      if(toSave.length>0) saveToStorage(key, toSave);
    }
  },[messages, storageReady, activeCompany]);
  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:projs", projs); },[projs, storageReady]);
  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:bal", balanceByDate); },[balanceByDate, storageReady]);
  // brief and cmvList are stored inside companies object - no separate effects needed
  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:companies", companies); },[companies, storageReady]);
  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:logo", appLogoUrl); },[appLogoUrl, storageReady]);
  useEffect(()=>{ if(storageReady) saveToStorage("smartcfo:activeCompany", activeCompany); },[activeCompany, storageReady]);
  // Load saved chat history when active company changes
  useEffect(()=>{
    if(!storageReady) return;
    (async()=>{
      try {
        const key = "smartcfo:chat:"+(activeCompany||"default");
        const r = await window.storage.get(key, true);
        const saved = r ? JSON.parse(r.value) : [];
        if(saved.length>0) {
          setMessages(prev=>{
            const welcome = prev.filter(m=>!m.ts);
            return [...welcome, ...saved];
          });
        }
      } catch(e) { /* no saved chat */ }
    })();
  },[activeCompany, storageReady]);

  // ── END STORAGE ─────────────────────────────────────────────

  useEffect(()=>{ chatEndRef.current?.scrollIntoView({behavior:"smooth"}); },[messages]);
  const addMsg = useCallback((role,content,from)=>setMessages(prev=>[...prev,{role,content,from:from||(role==="user"?"admin":"bot"),ts:Date.now()}]),[]);

  // Smart add transactions
  const handleAddTxs = useCallback((newTxs, source, newBal)=>{
    // Always read from ref to get the current activeCompany at call time (not stale closure)
    const cId = activeCompanyRef.current||"default";
    const taggedTxs = newTxs.map(t=>({...t, companyId: cId}));
    const incoming = [...new Set(taggedTxs.map(t=>ym(t.date)).filter(Boolean))];
    const existing = [...new Set(txs.filter(t=>t.source===source&&t.companyId===cId).map(t=>ym(t.date)).filter(Boolean))];
    const conflict = incoming.filter(p=>existing.includes(p));
    let added=0, dupes=0;
    setTxs(prev=>{
      const keys = new Set(prev.map(txKey));
      const toAdd = taggedTxs.filter(t=>{ const k=txKey(t); if(keys.has(k)){dupes++;return false;} added++; return true; });
      return [...prev,...toAdd];
    });
    if(newBal && Object.keys(newBal).length) {
      setBalanceByDate(prev=>{
        const merged = {...prev};
        for (const [date,bal] of Object.entries(newBal)) {
          if (!merged[date]) merged[date] = {};
          const key = source+":::"+cId;
          if (typeof merged[date]==="object") merged[date][key] = bal;
          else merged[date] = {[key]:bal};
        }
        return merged;
      });
    }
    return {added, dupes, periods:incoming, conflict};
  },[txs]);

  const handleAddProjs = useCallback((newProjs, companyId)=>{
    let added=0, updated=0;
    setProjs(prev=>{
      const result=[...prev];
      const taggedProjs = newProjs.map(p=>({...p, companyId: companyId||activeCompany||"default"}));
      taggedProjs.forEach(np=>{
        const exact=result.findIndex(p=>p.date===np.date&&p.channel===np.channel&&Math.abs((p.expectedRevenue||0)-(np.expectedRevenue||0))<0.01);
        const fuzzy=result.findIndex(p=>p.date===np.date&&p.channel===np.channel);
        if(exact!==-1) return;
        if(fuzzy!==-1){result[fuzzy]={...np,id:result[fuzzy].id};updated++;}
        else{result.push({...np,id:genId()});added++;}
      });
      return result;
    });
    return {added, updated};
  },[activeCompany]);

  const handleResolveTransfers = useCallback((decisions)=>{
    // decisions: {id: category}
    const resolved = Object.entries(decisions).map(([id,category])=>({id,category}));
    setTxs(prev=>{
      const pendingById = {};
      (transfersPending||[]).forEach(t=>{ pendingById[t.id]=t; });
      const toAdd = resolved.map(({id,category})=>({...pendingById[id], category, needsReview:false})).filter(Boolean);
      const keys = new Set(prev.map(txKey));
      const newOnes = toAdd.filter(t=>!keys.has(txKey(t)));
      return [...prev,...newOnes];
    });
    addMsg("assistant","✅ **"+resolved.length+" transferências** classificadas e adicionadas!");
  },[transfersPending, addMsg]);

  const handleClearAll = ()=>{
    const cId = activeCompany||"default";
    setTxs(prev=>prev.filter(t=>(t.companyId||"default")!==cId));
    setProjs(prev=>prev.filter(p=>(p.companyId||"default")!==cId));
    setBalanceByDate(prev=>{
      const next={};
      for (const [date, val] of Object.entries(prev)) {
        if (typeof val==="object") {
          const filtered=Object.fromEntries(Object.entries(val).filter(([k])=>!k.endsWith(":::"+cId)));
          if (Object.keys(filtered).length) next[date]=filtered;
        }
      }
      return next;
    });
    setPeriod("");
    setTransfersPending(null);
    addMsg("assistant","🗑️ Dados da empresa limpos.");
  };

  // Chat
  const sendMessage = async () => {
    if(!inputMsg.trim()||isTyping) return;
    const msg=inputMsg.trim(); setInputMsg(""); addMsg("user",msg); setIsTyping(true);

    // ── Build rich context from ALL available app data ──────────────
    const cId = activeCompany||"default";
    const compTxs = txs.filter(t=>(t.companyId||"default")===cId);

    // Month-by-month DRE summary (ALL months, not just selected)
    const allMonths = [...new Set(compTxs.map(t=>ym(t.competenceDate||t.date)).filter(Boolean))].sort();
    const monthlyDRE = allMonths.map(m => {
      const mTxs = compTxs.filter(t=>ym(t.competenceDate||t.date)===m);
      const rev    = mTxs.filter(t=>t.type==="credit"&&(t.category==="Receita de Vendas"||t.category==="Frete")).reduce((s,t)=>s+((t.grossAmount&&t.grossAmount>t.amount)?t.grossAmount:t.amount),0);
      const ret    = mTxs.filter(t=>t.type==="debit"&&t.category==="Devoluções").reduce((s,t)=>s+t.amount,0);
      const net    = rev - ret;
      const cogs   = mTxs.filter(t=>t.type==="debit"&&t.category==="CMV").reduce((s,t)=>s+t.amount,0);
      const mkt    = mTxs.filter(t=>t.type==="debit"&&t.category==="Marketing").reduce((s,t)=>s+t.amount,0);
      const fees   = mTxs.filter(t=>t.type==="debit"&&t.category==="Tarifas e Taxas").reduce((s,t)=>s+t.amount,0) + mTxs.filter(t=>t.type==="credit"&&t.fee>0).reduce((s,t)=>s+t.fee,0);
      const pers   = mTxs.filter(t=>t.type==="debit"&&t.category==="Pessoal").reduce((s,t)=>s+t.amount,0);
      const fix    = mTxs.filter(t=>t.type==="debit"&&(t.category==="Despesas Fixas"||t.category==="Fixas")).reduce((s,t)=>s+t.amount,0);
      const ops    = mTxs.filter(t=>t.type==="debit"&&(t.category==="Despesas Operacionais"||t.category==="Operacional")).reduce((s,t)=>s+t.amount,0);
      const frete  = mTxs.filter(t=>t.type==="debit"&&t.category==="Frete").reduce((s,t)=>s+t.amount,0);
      const inflow = mTxs.filter(t=>t.type==="credit").reduce((s,t)=>s+(t.grossAmount||t.amount),0);
      const outflow= mTxs.filter(t=>t.type==="debit").reduce((s,t)=>s+t.amount,0);
      const ebitda = net - cogs - mkt - fees - pers - fix - ops - frete;
      return m+": RecBruta="+fmt(rev)+" Devol="+fmt(ret)+" RecLiq="+fmt(net)+" CMV="+fmt(cogs)+" Mkt="+fmt(mkt)+" Pessoal="+fmt(pers)+" Fixas="+fmt(fix)+" Ops="+fmt(ops)+" Tarifas="+fmt(fees)+" EBITDA="+fmt(ebitda)+(net>0?" Margem="+Math.round(ebitda/net*100)+"%":"")+" | Entradas="+fmt(inflow)+" Saidas="+fmt(outflow)+" Liq="+fmt(inflow-outflow);
    });

    // Account balances
    const balLines = data?.latestBalances ? Object.entries(data.latestBalances).map(([acc,bal])=>acc+": "+fmt(bal)).join(" | ") : "";
    const totalBal = data?.totalCashBalance||0;

    // Runway / cash projection
    const rw = data?.runway;
    const rwLines = rw ? [
      "SaldoAtual="+fmt(rw.lastBalance),
      "QueimaDiaria="+fmt(rw.avgDailyBurn),
      "EntradaDiaria="+fmt(rw.avgDailyIncome),
      "LiqDiaria="+fmt((rw.avgDailyIncome||0)-(rw.avgDailyBurn||0)),
      rw.zeroDay?"ALERTA caixa zera em "+rw.zeroDay:""
    ].filter(Boolean).join(" | ") : "";

    // Projections from imported data
    const projLines = projs.filter(p=>(p.companyId||"default")===cId).slice(0,20)
      .map(p=>(p.month||p.date)+": "+fmt(p.amount||p.value||0)+" "+( p.description||"")).join("; ");

    // Build context string
    const ctx = [
      "EMPRESA: "+(brief||activeComp?.name||"ecommerce"),
      "PERÍODO SELECIONADO: "+(period||"todos os meses"),
      "MESES COM DADOS: "+allMonths.join(", "),
      "",
      "=== DRE MÊS A MÊS ===",
      ...monthlyDRE,
      "",
      "=== SALDOS POR CONTA ===",
      "Total em caixa: "+fmt(totalBal),
      balLines ? "Detalhado: "+balLines : "",
      "",
      "=== FLUXO DE CAIXA / RUNWAY ===",
      rwLines||"Sem dados de runway",
      projLines ? "=== PROJEÇÕES IMPORTADAS ===\n"+projLines : "",
    ].filter(s=>s!==undefined).join("\n");

    const chatSys = "Você é o SmartCFO, CFO Virtual especializado em ecommerce brasileiro. Responda em português, de forma direta e acionável. Use **negrito** para números importantes. Analise APENAS os dados do contexto abaixo — não invente dados. Quando o usuário perguntar sobre um mês específico, use SOMENTE os dados daquele mês no contexto.\n\nDADOS DO APP:\n"+ctx;
    try {

      const reqBody = {model:"claude-haiku-4-5-20251001",max_tokens:1024,system:chatSys,messages:[{role:"user",content:msg}]};
      console.log("Sending to API:", JSON.stringify(reqBody).slice(0,200));
      const res=await fetch("https://fbpcjxsspmjjhcmezswn.supabase.co/functions/v1/chat-proxy",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(reqBody)});
      const raw=await res.text();
      console.log("API response status:", res.status, "body:", raw.slice(0,300));
      let resp; try{resp=JSON.parse(raw);}catch{throw new Error("Parse falhou ("+res.status+"): "+raw.slice(0,300));}
      if(!res.ok||resp.error) throw new Error(resp.error?.message||resp.error?.type||("HTTP "+res.status));
      const reply=(resp.content||[]).find(b=>b.type==="text")?.text;
      if(!reply) throw new Error("Sem resposta da IA");
      setIsTyping(false); addMsg("assistant",reply);
    } catch(err){ setIsTyping(false); console.error("Chat API error:", err); addMsg("assistant","⚠️ Erro: "+err.message); }
  };

  // ── Render: Login screen
  if (authMode==="login") {
    return(
      <>
        <style>{"*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}html,body{height:100%;background:"+B.bg+";font-family:'Google Sans','Roboto',sans-serif}input:focus{outline:none;border-color:"+B.blue+"!important}button{font-family:inherit}"}</style>
        <LoginScreen companies={companies} appLogoUrl={appLogoUrl} onAdminLogin={()=>setAuthMode("admin")} onClientLogin={c=>{setClientCompany(c);setAuthMode("client");}}/>
      </>
    );
  }

  // ── Render: Client portal (read-only)
  if (authMode==="client") {
    return(
      <>
        <style>{"*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}html,body{height:100%;background:"+B.bg+";font-family:'Google Sans','Roboto',sans-serif}::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-thumb{background:"+B.border2+";border-radius:4px}@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}input:focus{outline:none;border-color:"+B.blue+"!important}button{font-family:inherit}"}</style>
        <ClientPortal allTxs={txs} projs={projs} balanceByDate={balanceByDate} months={[...new Set(txs.filter(t=>(t.companyId||"default")===(clientCompany?.id||"default")).map(t=>ym(t.date)).filter(Boolean))].sort()} period={period} setPeriod={setPeriod} company={clientCompany} appLogoUrl={appLogoUrl} onLogout={()=>{setAuthMode("login");setClientCompany(null);}}/>
      </>
    );
  }

  // ── Render: Admin app
  const uncategorized = data?.uncategorized||0;
  const anyLoading    = Object.values(loading).some(Boolean);
  const hasRunwayAlert= data?.runway?.zeroDay && Math.ceil((new Date(data.runway.zeroDay)-new Date(data.runway.lastDate))/86400000)<=30;

  const nav=[
    {key:"home",       icon:"◈", label:"Painel"},
    {key:"cashflow",   icon:"↕", label:"Fluxo de Caixa"},
    {key:"dre",        icon:"≡", label:"DRE"},
    {key:"runway",     icon:"💧",label:"Projeção Caixa",   badge:hasRunwayAlert?"!":0},
    {key:"projections",icon:"📈",label:"A Receber"},
    {key:"chat",       icon:"💬",label:"Chat"},
    {key:"categorize", icon:"🏷️",label:"Categ.",  badge:uncategorized},
    {key:"import",     icon:"📂",label:"Importar"},
    {key:"config",     icon:"⚙️",label:"Config"},
  ];
  const titles={import:"Importar Dados",home:"Painel",categorize:"Categorizar",dre:"DRE",cashflow:"Fluxo de Caixa",runway:"Projeção de Caixa",projections:"Projeções",config:"Configurações",chat:"Chat IA"};

  return(
    <>
      <style>{"@import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;700&family=Roboto:wght@400;500;700&display=swap'); *,*::before,*::after{box-sizing:border-box;margin:0;padding:0} html,body{height:100%;background:"+B.bg+";-webkit-tap-highlight-color:transparent} ::-webkit-scrollbar{width:4px;height:4px} ::-webkit-scrollbar-thumb{background:"+B.border2+";border-radius:4px} @keyframes fadeUp{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}} @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}} @keyframes pulse{0%,100%{opacity:0.4}50%{opacity:1}} input:focus,textarea:focus{outline:none;border-color:"+B.blue+"!important} input[type=month]{color-scheme:light} button,input,textarea,select{font-family:inherit}"}</style>

      <div style={{minHeight:"100dvh",display:"flex",flexDirection:"column",background:B.bg,fontFamily:"'Google Sans','Roboto',sans-serif",color:B.text}}>
        {/* HEADER */}
        <div style={{background:B.card,borderBottom:"1px solid "+B.border,padding:"clamp(8px,1.2vw,12px) clamp(12px,2vw,24px)",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",position:"sticky",top:0,zIndex:100}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            {appLogoUrl
              ? <img src={appLogoUrl} alt="Logo" style={{height:"clamp(28px,3.5vw,44px)",width:"auto",objectFit:"contain"}}/>
              : <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><path d="M8 3 Q8 10 14 10" stroke={B.red}   strokeWidth="3.5" strokeLinecap="round" fill="none"/><path d="M5 9 Q5 17 11 17" stroke={B.blue}  strokeWidth="3.5" strokeLinecap="round" fill="none"/><path d="M11 15 Q11 23 17 23" stroke={B.green}  strokeWidth="3.5" strokeLinecap="round" fill="none"/><path d="M8 21 Q8 29 14 29" stroke={B.yellow} strokeWidth="3.5" strokeLinecap="round" fill="none"/></svg>
            }
            <div style={{height:"clamp(20px,2.5vw,30px)",width:1,background:B.border}}/>
            <span style={{fontSize:"clamp(11px,1.3vw,14px)",fontWeight:600,color:B.textSub}}>{titles[screen]}</span>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {anyLoading&&<span style={{fontSize:9,color:B.yellow,fontWeight:600,animation:"pulse 1s infinite"}}>⏳</span>}
            {uncategorized>0&&<span style={{fontSize:9,background:B.red+"18",color:B.red,padding:"2px 8px",borderRadius:20,border:"1px solid "+B.red+"44",fontWeight:700}}>{uncategorized}⚠️</span>}
            {txs.length>0&&!anyLoading&&<span style={{fontSize:9,background:B.green+"18",color:B.green,padding:"2px 8px",borderRadius:20,border:"1px solid "+B.green+"44",fontWeight:700}}>{txs.filter(t=>(t.companyId||"default")===(activeCompany||"default")).length}/{txs.length} tx</span>}
            {storageReady&&txs.length>0&&<span style={{fontSize:9,color:B.green,fontWeight:600}} title="Dados salvos automaticamente">💾</span>}
            <button onClick={()=>setAuthMode("login")} style={{fontSize:10,background:"transparent",border:"1px solid "+B.border2,borderRadius:8,padding:"4px 10px",color:B.textSub,cursor:"pointer"}}>Sair</button>
          </div>
        </div>

        <div style={{display:"flex",flex:1,overflow:"hidden"}}>
          {/* DESKTOP SIDEBAR */}
          <style>{"@media(min-width:640px){.snav{display:flex!important}.bnav{display:none!important}.mcontent{max-width:none!important;margin:0!important}}@media(max-width:639px){.snav{display:none!important}.bnav{display:flex!important}.mcontent{max-width:500px;margin:0 auto}}"}</style>
          <div className="snav" style={{display:"none",flexDirection:"column",width:"clamp(160px,15vw,200px)",background:B.card,borderRight:"1px solid "+B.border,flexShrink:0,paddingTop:8,overflowY:"auto"}}>
            {nav.map(({key,icon,label,badge})=>{ const active=screen===key; return(
              <button key={key} onClick={()=>setScreen(key)} style={{display:"flex",alignItems:"center",gap:12,padding:"clamp(9px,1.2vw,12px) clamp(14px,1.8vw,20px)",border:"none",background:active?B.blue+"12":"transparent",cursor:"pointer",textAlign:"left",borderLeft:"3px solid "+(active?B.blue:"transparent"),transition:"all 0.15s",position:"relative",width:"100%"}}>
                <span style={{fontSize:"clamp(14px,1.6vw,17px)",filter:active?"none":"grayscale(0.6) opacity(0.7)"}}>{icon}</span>
                <span style={{fontSize:"clamp(11px,1.2vw,13px)",color:active?B.blue:B.textSub,fontWeight:active?700:400}}>{label}</span>
                {badge>0&&<span style={{marginLeft:"auto",minWidth:18,height:18,borderRadius:9,background:B.red,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:700,padding:"0 4px"}}>{badge>9?"9+":badge}</span>}
                {badge==="!"&&<span style={{marginLeft:"auto",width:8,height:8,borderRadius:"50%",background:B.red,animation:"pulse 2s infinite"}}/>}
              </button>
            );})}
          </div>

          {/* CONTENT */}
          <div className="mcontent" style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div key={screen} style={{flex:1,overflowY:screen==="chat"?"hidden":"auto",overflowX:"hidden",animation:"fadeUp 0.2s ease"}}>
              {screen==="import"      && <ImportScreen txs={txs} projs={projs} onAddTxs={handleAddTxs} onAddProjs={handleAddProjs} onClearAll={handleClearAll} loading={loading} setLoading={setLoading} addMsg={addMsg} transfersPending={transfersPending} setTransfersPending={setTransfersPending} onResolveTransfers={handleResolveTransfers} companies={companies} activeCompany={activeCompany} setActiveCompany={setActiveCompany}/>}
              {screen==="home"        && <HomeScreen data={data} months={months} period={period} setPeriod={setPeriod} uncategorized={uncategorized} onGoTo={setScreen} appLogo={appLogoUrl}/>}
              {screen==="categorize"  && <CategorizeScreen txs={txs} setTxs={setTxs} brief={brief} addMsg={addMsg} companyId={activeCompany||"default"} company={activeComp}/>}
              {screen==="dre"         && <DREScreen data={data} months={months} period={period} setPeriod={setPeriod}/>}
              {screen==="cashflow"    && <CashflowScreen data={data} months={months} period={period} setPeriod={setPeriod} setTxs={setTxs} company={activeComp}/>}
              {screen==="runway"      && <RunwayScreen runway={data?.runway}/>}
              {screen==="projections" && <ProjectionsScreen data={data}/>}
              {screen==="config"      && <ConfigScreen appLogoUrl={appLogoUrl} setAppLogoUrl={setAppLogoUrl} companies={companies} setCompanies={setCompanies} activeCompany={activeCompany} setActiveCompany={setActiveCompany} txs={txs} brief={brief} addMsg={addMsg}/>}
              {screen==="chat"        && <ChatScreen messages={messages} inputMsg={inputMsg} setInputMsg={setInputMsg} sendMessage={sendMessage} isTyping={isTyping} chatEndRef={chatEndRef}/>}
            </div>
          </div>
        </div>

        {/* MOBILE BOTTOM NAV */}
        <div className="bnav" style={{background:B.card,borderTop:"1px solid "+B.border,overflowX:"auto",flexShrink:0,paddingBottom:"env(safe-area-inset-bottom,0px)",boxShadow:"0 -1px 4px rgba(0,0,0,0.06)"}}>
          {nav.map(({key,icon,label,badge})=>{ const active=screen===key; return(
            <button key={key} onClick={()=>setScreen(key)} style={{flexShrink:0,minWidth:52,padding:"8px 4px 6px",border:"none",background:"transparent",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,borderTop:"2.5px solid "+(active?B.blue:"transparent"),transition:"all 0.15s",position:"relative"}}>
              <span style={{fontSize:15,filter:active?"none":"grayscale(0.7) opacity(0.5)"}}>{icon}</span>
              <span style={{fontSize:8,color:active?B.blue:B.textMut,fontWeight:active?700:400,whiteSpace:"nowrap"}}>{label}</span>
              {badge>0&&<span style={{position:"absolute",top:5,right:4,minWidth:14,height:14,borderRadius:7,background:B.red,fontSize:8,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:700,padding:"0 2px"}}>{badge>9?"9+":badge}</span>}
              {badge==="!"&&<span style={{position:"absolute",top:5,right:6,width:7,height:7,borderRadius:"50%",background:B.red,animation:"pulse 2s infinite"}}/>}
            </button>
          );})}
        </div>
      </div>
    </>
  );
}
